(function (global) {
  "use strict";

  // LAUNCH SIMPLIFICATION PASS — 2026-04-23
  // User-facing question wording, bridge copy, and result labels simplified
  // for cold visitors who do not know credit terminology. Logic is preserved;
  // the small-forgotten-card check is no longer asked as a separate question.

  // =========================================================
  // CREDIT OPTIMIZE — FREE-FIRST DIAGNOSTIC WITH $27 RESULT GATE + UNLOCK RESTORE + DEEP RESULT MODULES
  // HIGH-IQ LOGIC REBUILD
  // CHUNK 1 OF 7
  // FOUNDATION / CONSTANTS / HELPERS / QUICK-EXIT UTILITIES
  // =========================================================

  // ---------------------------------------------------------
  // DEVICE HELPERS
  // ---------------------------------------------------------

  function getViewportWidth() {
    return Math.max(
      document.documentElement ? document.documentElement.clientWidth || 0 : 0,
      window.innerWidth || 0
    );
  }

  function isMobileViewport() {
    return getViewportWidth() < 768;
  }

  function isSmallPhoneViewport() {
    return getViewportWidth() < 430 || (window.innerHeight || 0) < 760;
  }

  // ---------------------------------------------------------
  // ROOT PATHS / ENTRY TYPES
  // ---------------------------------------------------------

  const ROOT_ISSUES = {
    SCORE_DROPPED_AFTER_PAYDOWN: "score_dropped_after_paydown",
    SCORE_DROPPED_NO_REASON: "score_dropped_no_reason",
    NEW_NEGATIVE_FOUND: "new_negative_found",
    NOT_SURE: "not_sure",
    NO_SCORE: "no_score"
  };

  const RECENT_CHANGE = {
    PAID_DOWN_BALANCES: "paid_down_balances",
    OPENED_NEW_ACCOUNT: "opened_new_account",
    LIMIT_DECREASE_OR_CARD_CLOSED: "limit_decrease_or_card_closed",
    NOTHING_CHANGED: "nothing_changed",
    MISSED_PAYMENT_OR_NEGATIVE: "missed_payment_or_negative",
    NOT_SURE: "not_sure"
  };

  const ANSWERS = {
    YES: "yes",
    NO: "no",
    UNSURE: "unsure"
  };

  const PAYDOWN_TYPE = {
    ALL_TO_ZERO: "all_to_zero",
    ONE_CARD_SIGNIFICANT: "one_card_significant",
    SPREAD_ACROSS_MULTIPLE: "spread_across_multiple",
    ONLY_MINIMUMS: "only_minimums",
    NOT_SURE: "not_sure"
  };

  const CARD_COUNTS = {
    ONE: "1",
    TWO_TO_THREE: "2_3",
    FOUR_TO_FIVE: "4_5",
    SIX_PLUS: "6_plus"
  };

  const BALANCE_CARD_COUNTS = {
    NONE: "none",
    ONE: "1",
    TWO_TO_THREE: "2_3",
    FOUR_PLUS: "4_plus",
    MOST_OR_ALL: "most_or_all"
  };

  const UTIL_BUCKETS = {
    PAID_OFF: "paid_off",
    UNDER_10: "under_10",
    FROM_10_TO_29: "10_29",
    FROM_30_TO_49: "30_49",
    FROM_50_TO_74: "50_74",
    FROM_75_TO_89: "75_89",
    OVER_90: "90_plus",
    NOT_SURE: "not_sure"
  };

  const FILE_AGE = {
    NEVER_HAD_CREDIT: "never_had_credit",
    YOUNG_OR_STARTING: "young_or_starting",
    NEW_TO_US: "new_to_us",
    HAD_ACCOUNTS_BEFORE: "had_accounts_before",
    UNDER_6M: "under_6m",
    FROM_6M_TO_24M: "6m_24m",
    OVER_24M: "24m_plus",
    NOT_SURE: "not_sure"
  };

  const NEW_ACCOUNT_TYPE = {
    CREDIT_CARD: "credit_card",
    AUTO_LOAN: "auto_loan",
    PERSONAL_LOAN: "personal_loan",
    MORTGAGE: "mortgage",
    STORE_CARD: "store_card",
    NOT_SURE: "not_sure"
  };

  const NEGATIVE_TYPE = {
    LATE_PAYMENT: "late_payment",
    COLLECTION: "collection",
    CHARGE_OFF: "charge_off",
    UNKNOWN_ACCOUNT: "unknown_account",
    HARD_INQUIRY: "hard_inquiry",
    NOT_SURE: "not_sure"
  };

  // ---------------------------------------------------------
  // QUESTION IDS
  // ---------------------------------------------------------

  const QUESTION_IDS = {
    ROOT_ISSUE: "root_issue",
    RECENT_CHANGE: "recent_change",

    PAYDOWN_TYPE: "paydown_type",
    TOTAL_CARD_COUNT: "total_card_count",
    CARDS_WITH_BALANCES: "cards_with_balances",
    HIGHEST_UTIL_BUCKET: "highest_util_bucket",
    USED_AGAIN_AFTER_PAYDOWN: "used_again_after_paydown",
    PAYMENTS_REFLECTED_YET: "payments_reflected_yet",
    PAID_BEFORE_REPORTING: "paid_before_reporting",
    KNOW_REPORTING_DATE: "know_reporting_date",
    SMALL_CARD_OR_FORGOTTEN_BALANCE: "small_card_or_forgotten_balance",

    NEGATIVE_SEEN: "negative_seen",
    NEGATIVE_TYPE: "negative_type",
    RECOGNIZE_NEGATIVE: "recognize_negative",

    NEW_ACCOUNT_OPENED: "new_account_opened",
    NEW_ACCOUNT_TYPE: "new_account_type",
    NEW_ACCOUNT_WITHIN_90_DAYS: "new_account_within_90_days",

    LIMIT_DECREASE_OR_CLOSURE_CONFIRMED: "limit_decrease_or_closure_confirmed",

    ANY_UNKNOWN_ACTIVITY: "any_unknown_activity",
    ANY_BUSINESS_CARD_LATE: "any_business_card_late",

    FILE_AGE: "file_age",
    THIN_FILE_SUSPECTED: "thin_file_suspected",
    NO_SCORE_SITUATION: "no_score_situation",
    NO_SCORE_ACTIVE_ACCOUNTS: "no_score_active_accounts",
    NO_SCORE_NEGATIVES: "no_score_negatives",
    NO_SCORE_INTERFERENCE: "no_score_interference",
    NO_SCORE_OWN_ACCOUNT: "no_score_own_account",
    NO_SCORE_RECENT_ACCOUNT_OPENED: "no_score_recent_account_opened",
    NO_SCORE_USED_TO_HAVE_SCORE: "no_score_used_to_have_score",
    NO_SCORE_RECOGNIZE_NEGATIVE: "no_score_recognize_negative",
    NO_SCORE_NEGATIVE_TYPE: "no_score_negative_type",
    NO_SCORE_INTERFERENCE_TYPE: "no_score_interference_type",
    SYMPTOM: "symptom",
    NEW_ACCOUNT_TIMING: "new_account_timing",
    SCORE_DROP_AFTER_NEW_CREDIT: "score_drop_after_new_credit",
    NEW_CARD_BALANCE_RELEVANT: "new_card_balance_relevant"
  };

  // ---------------------------------------------------------
  // DIAGNOSES
  // ---------------------------------------------------------

  const DIAGNOSES = {
    ONE_CARD_HIGH_UTIL: "one_card_high_util",
    MANY_CARDS_REPORTING_BALANCES: "many_cards_reporting_balances",
    BALANCE_REAPPEARED_AFTER_PAYDOWN: "balance_reappeared_after_paydown",
    REPORTING_TIMING_MISMATCH: "reporting_timing_mismatch",
    PAYMENTS_NOT_REPORTED_YET: "payments_not_reported_yet",
    NEW_NEGATIVE_ACTIVITY: "new_negative_activity",
    LIMIT_DECREASE_OR_CARD_CLOSURE: "limit_decrease_or_card_closure",
    NEW_ACCOUNT_IMPACT: "new_account_impact",
    THIN_OR_SENSITIVE_FILE: "thin_or_sensitive_file",
    HIDDEN_BALANCE_OR_SMALL_CARD: "hidden_balance_or_small_card",
    UNKNOWN_OR_FRAUD_RISK: "unknown_or_fraud_risk",
    MIXED_SIGNAL_STACK: "mixed_signal_stack",
    INVESTIGATION_NEEDED: "investigation_needed",
    NO_SCORE_NO_FILE: "no_score_no_file",
    NO_SCORE_NEW_FILE_FORMING: "no_score_new_file_forming",
    NO_SCORE_THIN_FILE: "no_score_thin_file",
    NO_SCORE_INACTIVE_FILE: "no_score_inactive_file",
    NO_SCORE_ACTIVE_NOT_SCORING: "no_score_active_not_scoring",
    NO_SCORE_PREVIOUS_SCORE_LOST: "no_score_previous_score_lost",
    NO_SCORE_NEGATIVE_OVERPOWERING: "no_score_negative_overpowering",
    NO_SCORE_UNKNOWN_NEGATIVE: "no_score_unknown_negative",
    NO_SCORE_INTERFERENCE: "no_score_interference"
  };

  const DIAGNOSIS_META = {
    [DIAGNOSES.ONE_CARD_HIGH_UTIL]: {
      title: "One Card May Be Using Too Much of Its Limit",
      short: "One card appears to be carrying the strongest balance pressure.",
      grade: "red"
    },
    [DIAGNOSES.MANY_CARDS_REPORTING_BALANCES]: {
      title: "Too Many Cards Still Have Balances",
      short: "Balances may be spread across too many cards at once.",
      grade: "red"
    },
    [DIAGNOSES.BALANCE_REAPPEARED_AFTER_PAYDOWN]: {
      title: "Your Balance Likely Came Back After You Paid It Down",
      short: "A card may have been used again before the lower balance was reported.",
      grade: "red"
    },
    [DIAGNOSES.REPORTING_TIMING_MISMATCH]: {
      title: "Your Balance May Not Have Updated When You Expected",
      short: "What you paid and what showed up may not have matched yet.",
      grade: "orange"
    },
    [DIAGNOSES.PAYMENTS_NOT_REPORTED_YET]: {
      title: "Your Payments May Not Be Showing Yet",
      short: "The file may not have caught up to recent payments.",
      grade: "orange"
    },
    [DIAGNOSES.NEW_NEGATIVE_ACTIVITY]: {
      title: "A Negative Item May Be Driving the Drop",
      short: "A late payment, collection, or similar item may be overpowering other positives.",
      grade: "red"
    },
    [DIAGNOSES.LIMIT_DECREASE_OR_CARD_CLOSURE]: {
      title: "A Limit Cut or Card Closure May Be Hurting the File",
      short: "Less available credit can raise pressure even if balances did not change much.",
      grade: "red"
    },
    [DIAGNOSES.NEW_ACCOUNT_IMPACT]: {
      title: "A New Account May Be Causing a Temporary Drop",
      short: "New credit can lower scores before it helps.",
      grade: "orange"
    },
    [DIAGNOSES.THIN_OR_SENSITIVE_FILE]: {
      title: "Your File May Be Thin or Sensitive",
      short: "A younger or thinner file can react more sharply to smaller changes.",
      grade: "orange"
    },
    [DIAGNOSES.HIDDEN_BALANCE_OR_SMALL_CARD]: {
      title: "A Smaller or Overlooked Balance May Still Be Reporting",
      short: "A forgotten balance can quietly keep pressure on the file.",
      grade: "orange"
    },
    [DIAGNOSES.UNKNOWN_OR_FRAUD_RISK]: {
      title: "There May Be Unknown Activity on the File",
      short: "An account, inquiry, or negative item may not belong to you.",
      grade: "red"
    },
    [DIAGNOSES.MIXED_SIGNAL_STACK]: {
      title: "More Than One Issue May Be Hitting At Once",
      short: "The system sees layered pressure rather than one clean cause.",
      grade: "orange"
    },
    [DIAGNOSES.INVESTIGATION_NEEDED]: {
      title: "We Narrowed This To A Few Likely Causes",
      short: "Your answers point to a few likely causes instead of one single obvious trigger.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_NO_FILE]: {
      title: "This Looks More Like No Credit File Yet",
      short: "The issue appears to be not enough file history yet, not a normal score drop.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_NEW_FILE_FORMING]: {
      title: "Your File May Still Be Too New To Score Normally",
      short: "An account may exist, but it may still be too new or too early in reporting to generate a stable score.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_THIN_FILE]: {
      title: "This Looks More Like A Thin File",
      short: "There may be some history, but not enough depth for a stable score.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_INACTIVE_FILE]: {
      title: "This Looks More Like An Inactive File",
      short: "The credit report may have older history but not enough active current reporting now.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_ACTIVE_NOT_SCORING]: {
      title: "You May Have Accounts, But They May Not Be Producing A Score Right Now",
      short: "Accounts may exist, but not enough active, current, scoreable reporting appears to be supporting a visible score.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_PREVIOUS_SCORE_LOST]: {
      title: "You May Have Had A Score Before, But The File Structure Changed",
      short: "A score may have disappeared after closures, inactivity, or another major file change.",
      grade: "orange"
    },
    [DIAGNOSES.NO_SCORE_NEGATIVE_OVERPOWERING]: {
      title: "Negative Activity May Be Overpowering The Rest Of The File",
      short: "Negative activity may be outweighing the positive side of the profile.",
      grade: "red"
    },
    [DIAGNOSES.NO_SCORE_UNKNOWN_NEGATIVE]: {
      title: "An Unknown Negative Item May Be Interfering With Scoring",
      short: "There may be a negative item you do not recognize, which points to a file-accuracy or identity issue first.",
      grade: "red"
    },
    [DIAGNOSES.NO_SCORE_INTERFERENCE]: {
      title: "Something May Be Blocking Your Score From Showing",
      short: "A freeze, fraud alert, name mismatch, or reporting issue may be blocking your score from showing.",
      grade: "red"
    }
  };

  const SIGNAL_KEYS = {
    BALANCE_BEHAVIOR: "balance_behavior",
    CARD_DISTRIBUTION: "card_distribution",
    REPORTING_TIMING: "reporting_timing",
    NEGATIVE_RISK: "negative_risk",
    LIMIT_OR_CLOSURE: "limit_or_closure",
    NEW_CREDIT: "new_credit",
    FILE_STRENGTH: "file_strength",
    UNKNOWN_ACTIVITY: "unknown_activity"
  };

  const SIGNAL_LABELS = {
    [SIGNAL_KEYS.BALANCE_BEHAVIOR]: "High balances",
    [SIGNAL_KEYS.CARD_DISTRIBUTION]: "Cards with balances",
    [SIGNAL_KEYS.REPORTING_TIMING]: "Reporting timing",
    [SIGNAL_KEYS.NEGATIVE_RISK]: "Negative activity",
    [SIGNAL_KEYS.LIMIT_OR_CLOSURE]: "Available credit changes",
    [SIGNAL_KEYS.NEW_CREDIT]: "New credit activity",
    [SIGNAL_KEYS.FILE_STRENGTH]: "Credit history strength",
    [SIGNAL_KEYS.UNKNOWN_ACTIVITY]: "Unknown activity"
  };

  // ---------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------

  function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function clamp(num, min, max) {
    return Math.max(min, Math.min(max, num));
  }

  function uid() {
    return "co_" + Math.random().toString(36).slice(2, 10);
  }

  function truthyTri(value) {
    if (value === ANSWERS.YES || value === true) return true;
    if (value === ANSWERS.NO || value === false) return false;
    return "unsure";
  }

  function isYes(value) {
    return value === true || value === ANSWERS.YES;
  }

  function isNo(value) {
    return value === false || value === ANSWERS.NO;
  }

  function isUnsure(value) {
    return value === "unsure" || value === ANSWERS.UNSURE || value == null;
  }

  function pushUnique(arr, value) {
    if (value && !arr.includes(value)) arr.push(value);
    return arr;
  }

  function bucketToApproxCardCount(value) {
    switch (value) {
      case CARD_COUNTS.ONE:
      case BALANCE_CARD_COUNTS.ONE:
        return 1;
      case CARD_COUNTS.TWO_TO_THREE:
      case BALANCE_CARD_COUNTS.TWO_TO_THREE:
        return 2.5;
      case CARD_COUNTS.FOUR_TO_FIVE:
        return 4.5;
      case CARD_COUNTS.SIX_PLUS:
      case BALANCE_CARD_COUNTS.FOUR_PLUS:
      case BALANCE_CARD_COUNTS.MOST_OR_ALL:
        return 6;
      case BALANCE_CARD_COUNTS.NONE:
        return 0;
      default:
        return null;
    }
  }


  function buildSignal(status, label, reason, score, priority, detail) {
    return {
      status: status,
      label: label,
      reason: reason,
      score: score || 0,
      priority: priority || 0,
      detail: detail || ""
    };
  }

  function labelForConfidence(level) {
    if (level === "high") return "High confidence";
    if (level === "medium") return "Moderate confidence";
    return "";
  }

  function diagnosisTitle(key) {
    return (DIAGNOSIS_META[key] && DIAGNOSIS_META[key].title) || "Diagnosis";
  }

  function diagnosisShort(key) {
    return (DIAGNOSIS_META[key] && DIAGNOSIS_META[key].short) || "";
  }

  function diagnosisGrade(key) {
    return (DIAGNOSIS_META[key] && DIAGNOSIS_META[key].grade) || "orange";
  }

  function getStatusIcon(status) {
    if (status === "red") return "✕";
    if (status === "green") return "✓";
    return "•";
  }

  function getStatusClass(status) {
    if (status === "red") return "co-status-red";
    if (status === "green") return "co-status-green";
    return "co-status-orange";
  }

  function getPrimaryGradeClass(grade) {
    if (grade === "red") return "co-grade-red";
    if (grade === "green") return "co-grade-green";
    return "co-grade-orange";
  }

  function h(tag, attrs, children) {
    const el = document.createElement(tag);

    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        const value = attrs[key];
        if (value == null) return;

        if (key === "class") el.className = value;
        else if (key === "html") el.innerHTML = value;
        else if (key === "text") el.textContent = value;
        else if (key === "style" && typeof value === "object") Object.assign(el.style, value);
        else if (key.startsWith("on") && typeof value === "function") {
          el.addEventListener(key.slice(2).toLowerCase(), value);
        } else {
          el.setAttribute(key, value);
        }
      });
    }

    if (children && children.length) {
      children.forEach(function (child) {
        if (child == null) return;
        if (typeof child === "string") el.appendChild(document.createTextNode(child));
        else el.appendChild(child);
      });
    }

    return el;
  }

  function pickQuestionTitle(question) {
    if (!question) return "";
    return question.title || question.mobileTitle || "";
  }

  function pickOptionLabel(option) {
    if (!option) return "";
    return option.label || option.mobileLabel || "";
  }

  const QUESTIONS = {
    [QUESTION_IDS.ROOT_ISSUE]: {
      id: QUESTION_IDS.ROOT_ISSUE,
      group: "root",
      title: "What are you trying to understand?",
      mobileTitle: "What happened?",
      options: [
        {
          value: ROOT_ISSUES.SCORE_DROPPED_AFTER_PAYDOWN,
          label: "My score dropped after I paid cards down",
          mobileLabel: "Dropped after I paid cards"
        },
        {
          value: ROOT_ISSUES.SCORE_DROPPED_NO_REASON,
          label: "My score dropped and I don’t know why",
          mobileLabel: "Dropped and I don’t know why"
        },
        {
          value: ROOT_ISSUES.NEW_NEGATIVE_FOUND,
          label: "I saw a late payment, collection, or other negative",
          mobileLabel: "I saw a negative"
        },
        {
          value: ROOT_ISSUES.NOT_SURE,
          label: "I’m not sure what changed",
          mobileLabel: "I’m not sure"
        },
        {
          value: ROOT_ISSUES.NO_SCORE,
          label: "I don’t have a score right now",
          mobileLabel: "I don’t have a score"
        }
      ]
    },

    [QUESTION_IDS.RECENT_CHANGE]: {
      id: QUESTION_IDS.RECENT_CHANGE,
      group: "root",
      title: "What changed around the same time?",
      mobileTitle: "What changed around the same time?",
      options: [
        {
          value: RECENT_CHANGE.PAID_DOWN_BALANCES,
          label: "I paid down credit cards",
          mobileLabel: "Paid down cards"
        },
        {
          value: RECENT_CHANGE.OPENED_NEW_ACCOUNT,
          label: "I opened new credit",
          mobileLabel: "Opened new credit"
        },
        {
          value: RECENT_CHANGE.LIMIT_DECREASE_OR_CARD_CLOSED,
          label: "A card was closed or my limit changed",
             mobileLabel: "Card closed / limit changed"
        },
        {
          value: RECENT_CHANGE.MISSED_PAYMENT_OR_NEGATIVE,
          label: "A late payment, collection, or negative showed up",
          mobileLabel: "Late / collection / negative"
        },
        {
          value: RECENT_CHANGE.NOTHING_CHANGED,
          label: "Nothing I can think of changed",
          mobileLabel: "Nothing I can think of changed"
        },
        {
          value: RECENT_CHANGE.NOT_SURE,
          label: "I’m not sure",
          mobileLabel: "Not sure"
        }
      ]
    },

    [QUESTION_IDS.PAYDOWN_TYPE]: {
      id: QUESTION_IDS.PAYDOWN_TYPE,
      group: "utilization",
      title: "What did you pay down?",
      mobileTitle: "What did you pay down?",
      options: [
        {
          value: PAYDOWN_TYPE.ALL_TO_ZERO,
          label: "I paid all my cards to $0",
          mobileLabel: "Paid all cards to $0"
        },
        {
          value: PAYDOWN_TYPE.ONE_CARD_SIGNIFICANT,
          label: "I mostly paid down one card",
          mobileLabel: "Mostly paid one card"
        },
        {
          value: PAYDOWN_TYPE.SPREAD_ACROSS_MULTIPLE,
          label: "I paid down several cards",
          mobileLabel: "Paid several cards down"
        },
        {
          value: PAYDOWN_TYPE.ONLY_MINIMUMS,
          label: "I mostly made minimum payments",
          mobileLabel: "Mostly minimum payments"
        },
        {
          value: PAYDOWN_TYPE.NOT_SURE,
          label: "Not sure",
          mobileLabel: "Not sure"
        }
      ]

          },

    [QUESTION_IDS.TOTAL_CARD_COUNT]: {
      id: QUESTION_IDS.TOTAL_CARD_COUNT,
      group: "utilization",
      title: "How many credit cards do you have?",
      mobileTitle: "How many cards do you have?",
      options: [
        { value: CARD_COUNTS.ONE, label: "1", mobileLabel: "1" },
        { value: CARD_COUNTS.TWO_TO_THREE, label: "2–3", mobileLabel: "2–3" },
        { value: CARD_COUNTS.FOUR_TO_FIVE, label: "4–5", mobileLabel: "4–5" },
        { value: CARD_COUNTS.SIX_PLUS, label: "6+", mobileLabel: "6+" }
      ]
    },

    [QUESTION_IDS.CARDS_WITH_BALANCES]: {
      id: QUESTION_IDS.CARDS_WITH_BALANCES,
      group: "utilization",
      title: "How many of your cards still have a balance?",
      mobileTitle: "How many still have a balance?",
      options: [
        { value: BALANCE_CARD_COUNTS.NONE, label: "None", mobileLabel: "None" },
        { value: BALANCE_CARD_COUNTS.ONE, label: "1", mobileLabel: "1" },
        { value: BALANCE_CARD_COUNTS.TWO_TO_THREE, label: "2–3", mobileLabel: "2–3" },
        { value: BALANCE_CARD_COUNTS.FOUR_PLUS, label: "4+", mobileLabel: "4+" },
        { value: BALANCE_CARD_COUNTS.MOST_OR_ALL, label: "Most or all", mobileLabel: "Most or all" }
      ]
    },

    [QUESTION_IDS.HIGHEST_UTIL_BUCKET]: {
      id: QUESTION_IDS.HIGHEST_UTIL_BUCKET,
      group: "utilization",
      title: "About how much of your most used card is used right now?",
      mobileTitle: "How much of your most used card is used?",
      options: [
        { value: UTIL_BUCKETS.PAID_OFF, label: "No balance", mobileLabel: "No balance" },
        { value: UTIL_BUCKETS.UNDER_10, label: "Under 10%", mobileLabel: "Under 10%" },
        { value: UTIL_BUCKETS.FROM_10_TO_29, label: "10–29%", mobileLabel: "10–29%" },
        { value: UTIL_BUCKETS.FROM_30_TO_49, label: "30–49%", mobileLabel: "30–49%" },
        { value: UTIL_BUCKETS.FROM_50_TO_74, label: "50–74%", mobileLabel: "50–74%" },
        { value: UTIL_BUCKETS.FROM_75_TO_89, label: "75–89%", mobileLabel: "75–89%" },
        { value: UTIL_BUCKETS.OVER_90, label: "90%+", mobileLabel: "90%+" },
        { value: UTIL_BUCKETS.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.USED_AGAIN_AFTER_PAYDOWN]: {
      id: QUESTION_IDS.USED_AGAIN_AFTER_PAYDOWN,
      group: "utilization",
      title: "After paying your cards down, did you use them again right after?",
      mobileTitle: "Used them again right after?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.PAYMENTS_REFLECTED_YET]: {
      id: QUESTION_IDS.PAYMENTS_REFLECTED_YET,
      group: "timing",
      title: "Do the lower balances show on your credit report yet?",
      mobileTitle: "Do lower balances show yet?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.PAID_BEFORE_REPORTING]: {
      id: QUESTION_IDS.PAID_BEFORE_REPORTING,
      group: "timing",
      title: "Did you pay before the card updated its balance to the credit bureaus?",
      mobileTitle: "Paid before bureau update?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "I’m not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.KNOW_REPORTING_DATE]: {
      id: QUESTION_IDS.KNOW_REPORTING_DATE,
      group: "timing",
      title: "Do you know when your card sends its balance to the credit bureaus?",
      mobileTitle: "Know when it updates?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.SMALL_CARD_OR_FORGOTTEN_BALANCE]: {
      id: QUESTION_IDS.SMALL_CARD_OR_FORGOTTEN_BALANCE,
      group: "utilization",
      title: "Could a small leftover charge still be showing?",
      mobileTitle: "Small charge still showing?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEGATIVE_SEEN]: {
      id: QUESTION_IDS.NEGATIVE_SEEN,
      group: "negative",
      title: "Did a late payment, collection, charge-off, or other negative show up?",
      mobileTitle: "Did a negative show up?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEGATIVE_TYPE]: {
      id: QUESTION_IDS.NEGATIVE_TYPE,
      group: "negative",
      title: "What did you notice?",
      mobileTitle: "What did you notice?",
      options: [
        { value: NEGATIVE_TYPE.LATE_PAYMENT, label: "Late payment", mobileLabel: "Late payment" },
        { value: NEGATIVE_TYPE.COLLECTION, label: "Collection", mobileLabel: "Collection" },
        { value: NEGATIVE_TYPE.CHARGE_OFF, label: "Charge-off or serious missed-payment account", mobileLabel: "Charge-off" },
        { value: NEGATIVE_TYPE.UNKNOWN_ACCOUNT, label: "An account I do not recognize", mobileLabel: "Unknown account" },
        { value: NEGATIVE_TYPE.HARD_INQUIRY, label: "An application or inquiry I wasn’t expecting", mobileLabel: "Unexpected inquiry" },
        { value: NEGATIVE_TYPE.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.RECOGNIZE_NEGATIVE]: {
      id: QUESTION_IDS.RECOGNIZE_NEGATIVE,
      group: "negative",
      title: "Do you recognize this account or item?",
      mobileTitle: "Do you recognize this account or item?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEW_ACCOUNT_OPENED]: {
      id: QUESTION_IDS.NEW_ACCOUNT_OPENED,
      group: "new_credit",
      title: "Did you open any new credit recently?",
      mobileTitle: "Opened new credit recently?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEW_ACCOUNT_TYPE]: {
      id: QUESTION_IDS.NEW_ACCOUNT_TYPE,
      group: "new_credit",
      title: "What kind of new credit was it?",
      mobileTitle: "What kind of credit?",
      options: [
        { value: NEW_ACCOUNT_TYPE.CREDIT_CARD, label: "Credit card", mobileLabel: "Credit card" },
        { value: NEW_ACCOUNT_TYPE.AUTO_LOAN, label: "Auto loan / lease", mobileLabel: "Auto loan / lease" },
        { value: NEW_ACCOUNT_TYPE.PERSONAL_LOAN, label: "Personal loan", mobileLabel: "Personal loan" },
        { value: NEW_ACCOUNT_TYPE.MORTGAGE, label: "Mortgage", mobileLabel: "Mortgage" },
        { value: NEW_ACCOUNT_TYPE.STORE_CARD, label: "Store card", mobileLabel: "Store card" },
        { value: NEW_ACCOUNT_TYPE.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEW_ACCOUNT_WITHIN_90_DAYS]: {
      id: QUESTION_IDS.NEW_ACCOUNT_WITHIN_90_DAYS,
      group: "new_credit",
      title: "Was the new account opened within the last 90 days?",
      mobileTitle: "Opened within the last 90 days?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.LIMIT_DECREASE_OR_CLOSURE_CONFIRMED]: {
      id: QUESTION_IDS.LIMIT_DECREASE_OR_CLOSURE_CONFIRMED,
      group: "limit",
      title: "Did a card get closed or did a credit limit get lowered?",
      mobileTitle: "Closure or lower limit?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.ANY_UNKNOWN_ACTIVITY]: {
      id: QUESTION_IDS.ANY_UNKNOWN_ACTIVITY,
      group: "fraud",
      title: "Do you see anything you do not recognize — an account, balance, application, or alert?",
      mobileTitle: "Anything you don’t recognize?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.ANY_BUSINESS_CARD_LATE]: {
      id: QUESTION_IDS.ANY_BUSINESS_CARD_LATE,
      group: "negative",
      title: "Was there a late payment on a business card or loan that you personally guaranteed?",
      mobileTitle: "Business late tied to you?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.FILE_AGE]: {
      id: QUESTION_IDS.FILE_AGE,
      group: "file_strength",
      title: "How long have you had credit in your name?",
      mobileTitle: "How long have you had credit?",
      options: [
        { value: FILE_AGE.UNDER_6M, label: "Under 6 months", mobileLabel: "Under 6 months" },
        { value: FILE_AGE.FROM_6M_TO_24M, label: "6–24 months", mobileLabel: "6–24 months" },
        { value: FILE_AGE.OVER_24M, label: "2+ years", mobileLabel: "2+ years" },
        { value: FILE_AGE.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_SITUATION]: {
      id: QUESTION_IDS.NO_SCORE_SITUATION,
      group: "no_score",
      title: "Which one sounds most like your situation?",
      mobileTitle: "Which one sounds most like your situation?",
      options: [
        { value: FILE_AGE.NEVER_HAD_CREDIT, label: "Never had credit", mobileLabel: "Never had credit" },
        { value: FILE_AGE.YOUNG_OR_STARTING, label: "Young / just starting", mobileLabel: "Young / just starting" },
        { value: FILE_AGE.NEW_TO_US, label: "New to U.S.", mobileLabel: "New to U.S." },
        { value: FILE_AGE.HAD_ACCOUNTS_BEFORE, label: "Had accounts before", mobileLabel: "Had accounts before" },
        { value: FILE_AGE.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_ACTIVE_ACCOUNTS]: {
      id: QUESTION_IDS.NO_SCORE_ACTIVE_ACCOUNTS,
      group: "no_score",
      title: "Do you have any open accounts that are still updating on your credit report?",
      mobileTitle: "Open accounts still updating?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_NEGATIVES]: {
      id: QUESTION_IDS.NO_SCORE_NEGATIVES,
      group: "no_score",
      title: "Do you know of any late payments, collections, or negatives showing?",
      mobileTitle: "Any negatives showing?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_INTERFERENCE]: {
      id: QUESTION_IDS.NO_SCORE_INTERFERENCE,
      group: "no_score",
      title: "Could something be blocking your score from showing — like a freeze, fraud alert, name mismatch, or reporting problem?",
      mobileTitle: "Freeze, fraud alert, or mismatch?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_OWN_ACCOUNT]: {
      id: QUESTION_IDS.NO_SCORE_OWN_ACCOUNT,
      group: "no_score",
      title: "Do you currently have any credit account in your own name?",
      mobileTitle: "Account in your name?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_RECENT_ACCOUNT_OPENED]: {
      id: QUESTION_IDS.NO_SCORE_RECENT_ACCOUNT_OPENED,
      group: "no_score",
      title: "Was that account opened within the last 6 months?",
      mobileTitle: "Opened within the last 6 months?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_USED_TO_HAVE_SCORE]: {
      id: QUESTION_IDS.NO_SCORE_USED_TO_HAVE_SCORE,
      group: "no_score",
      title: "Did you have a visible credit score before?",
      mobileTitle: "Did you have a score before?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_RECOGNIZE_NEGATIVE]: {
      id: QUESTION_IDS.NO_SCORE_RECOGNIZE_NEGATIVE,
      group: "no_score",
      title: "Do you recognize the negative account or item?",
      mobileTitle: "Do you recognize the negative?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_NEGATIVE_TYPE]: {
      id: QUESTION_IDS.NO_SCORE_NEGATIVE_TYPE,
      group: "no_score",
      title: "What type of issue did you notice?",
      mobileTitle: "What type of issue was it?",
      options: [
        { value: NEGATIVE_TYPE.LATE_PAYMENT, label: "Late payment", mobileLabel: "Late payment" },
        { value: NEGATIVE_TYPE.COLLECTION, label: "Collection", mobileLabel: "Collection" },
        { value: NEGATIVE_TYPE.CHARGE_OFF, label: "Charge-off", mobileLabel: "Charge-off" },
        { value: NEGATIVE_TYPE.UNKNOWN_ACCOUNT, label: "Account I don't recognize", mobileLabel: "Unknown account" },
        { value: NEGATIVE_TYPE.NOT_SURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NO_SCORE_INTERFERENCE_TYPE]: {
      id: QUESTION_IDS.NO_SCORE_INTERFERENCE_TYPE,
      group: "no_score",
      title: "What sounds most likely?",
      mobileTitle: "What sounds most likely?",
      options: [
        { value: "freeze", label: "Credit freeze or fraud alert", mobileLabel: "Freeze / fraud" },
        { value: "mismatch", label: "Name, identity, or bureau mismatch", mobileLabel: "Identity mismatch" },
        { value: "reporting", label: "Account not reporting correctly", mobileLabel: "Account not reporting correctly" },
        { value: "not_sure", label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.SYMPTOM]: {
      id: QUESTION_IDS.SYMPTOM,
      group: "symptom",
      title: "What are you noticing?",
      mobileTitle: "What are you noticing?",
      options: [
        { value: "score_dropped", label: "Score dropped", mobileLabel: "Score dropped" },
        { value: "denied_credit", label: "Denied credit", mobileLabel: "Denied credit" },
        { value: "expected_increase", label: "Expected increase didn’t happen", mobileLabel: "Expected increase didn’t happen" },
        { value: "something_wrong", label: "Something looks wrong", mobileLabel: "Something looks wrong" },
        { value: "just_checking", label: "Just checking", mobileLabel: "Just checking" }
      ]
    },

    [QUESTION_IDS.NEW_ACCOUNT_TIMING]: {
      id: QUESTION_IDS.NEW_ACCOUNT_TIMING,
      group: "new_credit",
      title: "When was it opened?",
      mobileTitle: "When was it opened?",
      options: [
        { value: "0_30", label: "0–30 days", mobileLabel: "0–30 days" },
        { value: "1_3", label: "1–3 months", mobileLabel: "1–3 months" },
        { value: "3_6", label: "3–6 months", mobileLabel: "3–6 months" },
        { value: "not_sure", label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.SCORE_DROP_AFTER_NEW_CREDIT]: {
      id: QUESTION_IDS.SCORE_DROP_AFTER_NEW_CREDIT,
      group: "new_credit",
      title: "Did the score drop after that?",
      mobileTitle: "Did the score drop after that?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.NEW_CARD_BALANCE_RELEVANT]: {
      id: QUESTION_IDS.NEW_CARD_BALANCE_RELEVANT,
      group: "new_credit",
      title: "If it was a card, did it also report with a balance?",
      mobileTitle: "Did the new card report with a balance?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    },

    [QUESTION_IDS.THIN_FILE_SUSPECTED]: {
      id: QUESTION_IDS.THIN_FILE_SUSPECTED,
      group: "file_strength",
      title: "Do you only have a small number of accounts on your credit report?",
      mobileTitle: "Only a few accounts?",
      options: [
        { value: ANSWERS.YES, label: "Yes", mobileLabel: "Yes" },
        { value: ANSWERS.NO, label: "No", mobileLabel: "No" },
        { value: ANSWERS.UNSURE, label: "Not sure", mobileLabel: "Not sure" }
      ]
    }
  };

  // ---------------------------------------------------------
  // DEFAULT STATE
  // ---------------------------------------------------------

  const DEFAULT_STATE = {
    sessionId: uid(),
    currentQuestionId: QUESTION_IDS.ROOT_ISSUE,
    asked: [],
    answers: {},
    complete: false,

    rootIssue: null,
    recentChange: null,

    paydownType: null,
    totalCardCount: null,
    cardsWithBalances: null,
    highestUtilBucket: null,
    usedAgainAfterPaydown: null,
    paymentsReflectedYet: null,
    paidBeforeReporting: null,
    knowReportingDate: null,
    smallCardOrForgottenBalance: null,

    negativeSeen: null,
    negativeType: null,
    recognizeNegative: null,

    newAccountOpened: null,
    newAccountType: null,
    newAccountWithin90Days: null,

    limitDecreaseOrClosureConfirmed: null,
    anyUnknownActivity: null,
    anyBusinessCardLate: null,

    fileAge: null,
    thinFileSuspected: null,
    noScoreSituation: null,
    noScoreActiveAccounts: null,
    noScoreNegatives: null,
    noScoreInterference: null,
    noScoreOwnAccount: null,
    noScoreRecentAccountOpened: null,
    noScoreUsedToHaveScore: null,
    noScoreRecognizeNegative: null,
    noScoreNegativeType: null,
    noScoreInterferenceType: null,
    symptom: null,
    newAccountTiming: null,
    scoreDropAfterNewCredit: null,
    newCardBalanceRelevant: null,

    lane: null,
    cinematicSeen: false,
    wowSeen: false,
    bridgeSeen: false,
    expandedSignalKey: null,
    routeReason: "",
    earlyExitReason: "",
    scoreAnimated: false,

    result: {
      primaryDiagnosis: DIAGNOSES.INVESTIGATION_NEEDED,
      secondaryDiagnoses: [],
      confidence: "low",
      optimizeIndex: 54,
      signalTable: {},
      whyFlagged: [],
      overlookedAreas: [],
      summary: "",
      explanation: "",
      primaryAction: "",
      secondaryNote: "",
      nextWindow: "Review timing and current reporting data.",
      wowMessage: "",
      whatWeFound: "",
      whyWeThinkThat: "",
      whatMakesThisDifferent: "",
      whatMattersFirst: "",
      whatYouToldUs: "",
      whySystemFlagged: "",
      whyThisWon: "",
      whatToCheckNext: ""
    }
  };

  // ---------------------------------------------------------
  // ENGINE
  // ---------------------------------------------------------


  // ---------------------------------------------------------
  // UI SHELL STUBS — PRIVATE BRAIN REMOVED
  // Vercel backend owns routing + diagnosis.
  // ---------------------------------------------------------

const CO_DIAGNOSE_ENDPOINT = "https://project-fhpyx.vercel.app/api/diagnose";

  function countAnsweredCoreSignals(state) {
    return state && state.asked ? state.asked.length : 0;
  }

  function buildSignal(status, label, reason, score, priority, detail) {
    return {
      status: status || "orange",
      label: label || "Review item",
      reason: reason || "Evaluated from your answers.",
      score: score || 0,
      priority: priority || 0,
      detail: detail || reason || ""
    };
  }

  function buildCinematicLines(state) {
    const custom = state && state.cinematicLines;
    if (Array.isArray(custom) && custom.length) return custom;
    return [
      "Reviewing recent score-change pattern",
      "Checking balance and reporting behavior",
      "Scanning for stronger negative issues",
      "Ranking likely causes",
      "Preparing your diagnosis"
    ];
  }

  function getMicroBridge(state, questionId) {
    if (state && state.microBridge) return state.microBridge;
    const map = {};
    map[QUESTION_IDS.RECENT_CHANGE] = "Most score drops come from something here — even if it did not seem important.";
    map[QUESTION_IDS.PAYDOWN_TYPE] = "That helps — now let’s see what changed after the payment.";
    map[QUESTION_IDS.CARDS_WITH_BALANCES] = "This helps us understand how your balances are affecting your score.";
    map[QUESTION_IDS.HIGHEST_UTIL_BUCKET] = "Sometimes one card affects your score more than the others.";
    map[QUESTION_IDS.USED_AGAIN_AFTER_PAYDOWN] = "This happens more often than people think.";
    map[QUESTION_IDS.NEGATIVE_TYPE] = "That points us in the right direction — now tell us what showed up.";
    map[QUESTION_IDS.RECOGNIZE_NEGATIVE] = "Now let’s separate something you recognize from something that may not belong to you.";
    map[QUESTION_IDS.NEW_ACCOUNT_TYPE] = "That helps — now let’s identify the type of new credit.";
    map[QUESTION_IDS.NEW_ACCOUNT_WITHIN_90_DAYS] = "Now let’s see how recent it was.";
    map[QUESTION_IDS.LIMIT_DECREASE_OR_CLOSURE_CONFIRMED] = "Now let’s check if your available credit changed underneath you.";
    map[QUESTION_IDS.FILE_AGE] = "Now let’s get a quick read on how established your credit is.";
    map[QUESTION_IDS.NO_SCORE_SITUATION] = "Now let’s narrow why a score may not be showing at all.";
    return map[questionId] || "";
  }

  function getWowReassurance(state) {
    const r = state && state.result ? state.result : {};
    return r.wowReassurance || "We found the clearest explanation.";
  }

  function getWowMessage(state) {
    const r = state && state.result ? state.result : {};
    return r.wowMessage || r.explanation || r.summary || "This is the clearest issue affecting the credit profile right now.";
  }


  function getRankTagText(rankTag) {
    if (rankTag === "main") return "Main issue";
    if (rankTag === "secondary") return "Also affecting it";
    if (rankTag === "tertiary") return "Worth checking";
    return "";
  }

  function getSignalDisplayLabel(signalKey, state, fallback) {
    return SIGNAL_LABELS[signalKey] || fallback || "Review item";
  }

  function getDiagnosisProgressState(state, result) {
    const answered = countAnsweredCoreSignals(state || {});
    const progress = clamp(24 + answered * 9, 24, 94);
    const table = (result && result.signalTable) || {};
    const keys = Object.keys(table).slice(0, 4);
    const bars = keys.length ? keys.map(function (key) {
      const signal = table[key] || {};
      return {
        key: key,
        label: signal.label || SIGNAL_LABELS[key] || "Review Item",
        shortLabel: signal.label || SIGNAL_LABELS[key] || "Review",
        width: clamp(25 + (signal.score || 0) * 2, 18, 100)
      };
    }) : [
      { key: "balance", label: "Balance Pressure", shortLabel: "Balance", width: progress },
      { key: "timing", label: "Reporting Timing", shortLabel: "Timing", width: clamp(progress - 8, 12, 100) },
      { key: "negative", label: "Negative Activity", shortLabel: "Negative", width: clamp(progress - 14, 12, 100) },
      { key: "file", label: "File Strength", shortLabel: "File", width: clamp(progress - 20, 12, 100) }
    ];
    return { progress: progress, phase: answered < 2 ? "Scanning" : answered < 4 ? "Calculating" : "Locking diagnosis", bars: bars };
  }

  function buildMiniSignalWidget(state) {
    const progress = getDiagnosisProgressState(state || {}, (state && state.result) || {});
    return h("div", { class: "co-mini-signal-widget" + (isSmallPhoneViewport() ? " co-mini-signal-widget-small" : "") }, [
      h("div", { class: "co-mini-signal-head" }, [
        h("span", { class: "co-mini-signal-kicker" }, [isSmallPhoneViewport() ? "Live build" : "Live diagnosis build"]),
        h("span", { class: "co-mini-signal-phase" }, [progress.phase || "Calculating"])
      ]),
      h("div", { class: "co-mini-signal-list" }, (progress.bars || []).slice(0, 4).map(function (bar, index) {
        const label = isSmallPhoneViewport() ? (bar.shortLabel || bar.label) : bar.label;
        return h("div", { class: "co-mini-signal-row" + (index === 0 ? " is-active" : "") }, [
          h("span", { class: "co-mini-signal-label" }, [label]),
          h("span", { class: "co-mini-signal-track" }, [
            h("span", { class: "co-mini-signal-fill", style: { width: String(bar.width) + "%" } })
          ])
        ]);
      }))
    ]);
  }

function buildDetailedExplanation(signalKey, state) {
  const result = (state && state.result) || {};
  const table = result.signalTable || {};
  const signal = table[signalKey] || {};
  const detail = signal.detail || {};
  const primary = result.primaryDiagnosis || "";

  function pack(body, sections, action) {
    return {
      body: body || signal.reason || "This item was evaluated based on your answers.",
      why: "",
      action: action || "Use this as an education-first checklist. Verify the item directly on your credit reports before taking action.",
      sections: sections || []
    };
  }

  function baseReason() {
    if (typeof detail === "object" && detail.body) return detail.body;
    if (typeof detail === "string" && detail) return detail;
    return signal.reason || "This signal was evaluated from your answers and compared against the other likely causes.";
  }

  if (primary === DIAGNOSES.UNKNOWN_OR_FRAUD_RISK || signalKey === SIGNAL_KEYS.UNKNOWN_ACTIVITY) {
    return pack(
      "Unknown activity is one of the most important signals to verify because it can mean several different things: a creditor name you do not recognize, an old account reporting under a different company, a collection agency using a different name, a data/reporting error, or possible identity theft.",
      [
        {
          title: "What this may mean",
          body: "If an account, inquiry, balance, address, phone number, collection, or creditor name looks unfamiliar, do not assume the score change is normal. First separate harmless name confusion from activity that may not belong to you. Some creditors report under parent companies or collection agencies, while truly unfamiliar activity can point to identity theft or mixed-file reporting."
        },
        {
          title: "Why it matters",
          body: "Unknown accounts or inquiries can affect both score and lender confidence. If the item is inaccurate or fraudulent, waiting too long can allow more damage to appear. If it is simply a renamed creditor or transferred collection, the right next step is different."
        },
        {
          title: "What to verify first",
          body: "Pull or review all three bureau reports, compare the creditor name, open date, balance, account number fragments, inquiry date, address history, and collection agency name. Look for matching activity across bureaus and check whether the item connects to an old card, loan, medical bill, authorized-user account, or collection transfer."
        },
        {
          title: "What not to assume",
          body: "Do not assume every unfamiliar name is fraud, but also do not ignore it because the score drop seems small. A small unknown inquiry or account can be the first visible sign of a larger identity issue."
        },
        {
          title: "When to escalate",
          body: "If you cannot connect the item to anything you recognize, consider placing a fraud alert or credit freeze, contacting the creditor directly through a verified phone number, documenting what you found, and using the bureau dispute or identity-theft process where appropriate."
        }
      ],
      "First move: verify the item across all three credit reports before treating the score change as normal."
    );
  }

  if (primary === DIAGNOSES.NEW_NEGATIVE_ACTIVITY || signalKey === SIGNAL_KEYS.NEGATIVE_RISK) {
    return pack(
      "Negative activity can outweigh almost every other positive factor because it changes how risky the file looks. A late payment, collection, charge-off, unknown negative, or serious delinquency can drive a drop even if balances improved.",
      [
        {
          title: "What this may mean",
          body: "The system saw signs that a negative item may be more important than normal balance movement. This could be a recent late payment, collection, charge-off, unexpected inquiry, business-card late payment tied to a personal guarantee, or an account you do not recognize."
        },
        {
          title: "Why it matters",
          body: "Negative information can affect score, approvals, pricing, and lender confidence. Even one new serious negative can overpower utilization improvements or recent payments."
        },
        {
          title: "What to check first",
          body: "Check the date reported, date of first delinquency if available, account status, balance, creditor/collector name, and whether the item appears on one bureau or all three. If it is a late payment, verify the due date, payment posted date, autopay status, and whether the account was personal, joint, business-guaranteed, or authorized user."
        },
        {
          title: "What not to assume",
          body: "Do not assume paying a collection automatically removes it, and do not assume a business account can never affect personal credit. If you personally guaranteed the account or are legally responsible, it may still report personally."
        },
        {
          title: "When to escalate",
          body: "If the item is unfamiliar, inaccurate, duplicated, wrong-balance, wrong-date, or tied to identity theft, treat that as a verification/escalation issue instead of guessing. If you recognize it, focus first on understanding the reporting status and your available options."
        }
      ],
      "First move: identify the exact negative item, bureau, date, creditor, and whether you recognize it."
    );
  }

  if (primary === DIAGNOSES.REPORTING_TIMING_MISMATCH || primary === DIAGNOSES.PAYMENTS_NOT_REPORTED_YET || signalKey === SIGNAL_KEYS.REPORTING_TIMING) {
    return pack(
      "Reporting timing is one of the most common reasons a score does not react the way someone expects. Your score usually reacts to the balance that was reported to the bureaus, not necessarily the balance you see today in your banking app.",
      [
        {
          title: "What this may mean",
          body: "A payment may have posted after the lender already sent the balance snapshot to the credit bureaus, or the lower balance may not have updated yet. This can make it look like the score ignored your payment even though the report simply has not caught up."
        },
        {
          title: "Why it matters",
          body: "Credit scoring systems generally evaluate reported data. If a card reported a high balance before your payment posted, the score can still reflect the older balance until the next update."
        },
        {
          title: "What to verify first",
          body: "Compare three dates: the payment date, the statement closing/reporting date, and the date the balance updated on the credit report. The key question is not just whether you paid, but whether the lower balance was reported."
        },
        {
          title: "What not to assume",
          body: "Do not assume a score drop after payment means the payment hurt you. In many cases, the score is still reacting to an old snapshot, a balance that reappeared, or a different card that reported before the payment showed."
        },
        {
          title: "Timeline expectation",
          body: "Many card balance updates show around the next statement/reporting cycle. If the report still shows the old balance, the score may not fully reflect the payment until the creditor sends the next update."
        }
      ],
      "First move: compare the reported balance date against the payment date before making another move."
    );
  }

  if (primary === DIAGNOSES.ONE_CARD_HIGH_UTIL || primary === DIAGNOSES.BALANCE_REAPPEARED_AFTER_PAYDOWN || signalKey === SIGNAL_KEYS.BALANCE_BEHAVIOR) {
    return pack(
      "A single high-balance card can create more pressure than people expect, even when the rest of the file looks fine. The system flagged balance behavior because one card or one balance pattern may be carrying the strongest score pressure.",
      [
        {
          title: "What this may mean",
          body: "One card may still be reporting a balance that is high compared with its limit, or a balance may have reappeared after payoff because the card was used again before the lower balance reported."
        },
        {
          title: "Why it matters",
          body: "Utilization is not only about total balances. A single card near its limit can still pressure the file because it makes the account look more heavily used. A 0% APR promotion does not remove the utilization effect if the balance is high."
        },
        {
          title: "What to verify first",
          body: "Check the highest-balance card, its credit limit, the balance currently reporting, and whether the reported balance is different from the balance you see today. Also check whether a small purchase, subscription, annual fee, or residual balance posted after payoff."
        },
        {
          title: "What not to assume",
          body: "Do not assume paying several cards a little is always better than lowering the highest-pressure card. The strongest pressure often comes from the card with the highest reported usage compared with its limit."
        },
        {
          title: "Timeline expectation",
          body: "If the issue is only reported utilization, score movement often depends on when the lower balance is next reported. The practical target is getting the right balance reported, not just making a payment."
        }
      ],
      "First move: identify the highest reported card balance compared with its limit."
    );
  }

  if (primary === DIAGNOSES.MANY_CARDS_REPORTING_BALANCES || signalKey === SIGNAL_KEYS.CARD_DISTRIBUTION) {
    return pack(
      "The number of cards reporting balances can matter even when no single card looks extreme. This signal means the issue may be spread across the file rather than concentrated in one account.",
      [
        {
          title: "What this may mean",
          body: "Several cards may still be reporting balances at the same time. Even smaller balances can create a file that looks more active or pressured when many revolving accounts report usage together."
        },
        {
          title: "Why it matters",
          body: "Scoring can react to both how much revolving debt is reporting and how that debt is distributed. Too many cards with balances can make the file look less clean even if total utilization is not terrible."
        },
        {
          title: "What to verify first",
          body: "List every open card, the balance currently reporting, and the reporting/statement date. Separate cards that are truly reporting zero from cards that only look paid off in the banking app."
        },
        {
          title: "What not to assume",
          body: "Do not assume the smallest balance is harmless. A small subscription, annual fee, or leftover charge can keep a card reporting a balance and change how many accounts show activity."
        },
        {
          title: "Practical direction",
          body: "The first goal is usually to understand which cards are reporting balances, then decide which balances should be reduced or timed differently for the next reporting cycle."
        }
      ],
      "First move: count how many cards are reporting balances right now, not how many you believe are paid off."
    );
  }

  if (primary === DIAGNOSES.LIMIT_DECREASE_OR_CARD_CLOSURE || signalKey === SIGNAL_KEYS.LIMIT_OR_CLOSURE) {
    return pack(
      "A credit-limit decrease or card closure can hurt the file because it reduces available credit. That can raise utilization pressure even when your spending or balances did not change much.",
      [
        {
          title: "What this may mean",
          body: "A card may have been closed, restricted, or lowered by the issuer. If a balance remains on a closed or reduced-limit card, utilization pressure can increase quickly."
        },
        {
          title: "Why it matters",
          body: "Available credit is part of the utilization picture. If your limit drops from $5,000 to $2,000 while the balance stays the same, the same balance suddenly looks heavier."
        },
        {
          title: "What to verify first",
          body: "Check each card’s current limit against the prior limit, whether the card is open or closed, whether a balance remains, and whether the issuer recently changed the account terms."
        },
        {
          title: "What not to assume",
          body: "Do not assume nothing changed just because you did not spend more. The lender may have changed the limit or status behind the scenes."
        },
        {
          title: "Practical direction",
          body: "If the account is closed or the limit was cut, focus on the balance-to-limit pressure and whether another card is now carrying too much of the available-credit load."
        }
      ],
      "First move: verify every open card’s current credit limit and status."
    );
  }

  if (primary === DIAGNOSES.NEW_ACCOUNT_IMPACT || signalKey === SIGNAL_KEYS.NEW_CREDIT) {
    return pack(
      "New credit can create a temporary drop before it helps. The system flagged this because a new account, inquiry, or recently opened loan/card may be changing the file’s age, inquiry, or balance profile.",
      [
        {
          title: "What this may mean",
          body: "A new credit card, auto loan, personal loan, mortgage, store card, or inquiry may have appeared recently. New accounts can temporarily lower score because the file has new activity that has not yet aged."
        },
        {
          title: "Why it matters",
          body: "New accounts can affect average age, inquiry count, account mix, and early balance reporting. A new card that reports with a balance can also create utilization pressure right away."
        },
        {
          title: "What to verify first",
          body: "Check the open date, inquiry date, first reported balance, account type, and whether the new account is showing on one bureau or all three. For a new card, check whether it reported with a balance before you expected it to."
        },
        {
          title: "What not to assume",
          body: "Do not assume new credit is always bad. It can help over time, but right before a mortgage or major application, unnecessary new accounts can create avoidable short-term pressure."
        },
        {
          title: "Timeline expectation",
          body: "The impact often changes as the account ages, payment history builds, and balances report correctly. The key is whether the drop lines up with the new account timeline."
        }
      ],
      "First move: match the score change date against the new account or inquiry date."
    );
  }

  if (primary === DIAGNOSES.THIN_OR_SENSITIVE_FILE || signalKey === SIGNAL_KEYS.FILE_STRENGTH) {
    return pack(
      "A thin or sensitive credit file can react more sharply to changes that might barely move a stronger file. Smaller shifts can look bigger when there are fewer accounts, less age, or limited positive history supporting the score.",
      [
        {
          title: "What this may mean",
          body: "Your file may not have enough depth to absorb normal changes. A balance spike, new account, inquiry, late payment, or even one card reporting differently can carry more weight."
        },
        {
          title: "Why it matters",
          body: "Credit profiles with fewer accounts or shorter history often have less stability. That means the same action can create a bigger score swing compared with someone who has a thicker, older file."
        },
        {
          title: "What to verify first",
          body: "Check how many open accounts you have, how old they are, whether they are active, whether you have your own primary accounts, and whether most of your history depends on one account."
        },
        {
          title: "What not to assume",
          body: "Do not assume the problem is only the score. The deeper issue may be file structure: age, account depth, account mix, and whether enough positive history is reporting consistently."
        },
        {
          title: "Practical direction",
          body: "For thin files, the goal is usually stable reporting and gradual structure-building. Avoid stacking unnecessary new applications right before a major credit need."
        }
      ],
      "First move: identify whether the file has enough active, positive accounts to support a stable score."
    );
  }

  if (primary && String(primary).indexOf("NO_SCORE") === 0) {
    return pack(
      "A missing score is usually a file-structure issue, not a normal score drop. The system flagged this because the file may not have enough active scoreable information, or something may be interfering with score generation.",
      [
        {
          title: "What this may mean",
          body: "There may be no active credit file yet, the file may be too new, older accounts may have gone inactive, or a freeze, fraud alert, mismatch, or reporting issue may be interfering with score visibility."
        },
        {
          title: "Why it matters",
          body: "Without enough current scoreable data, lenders may not see a stable score even if some history exists. A no-score situation needs a different approach than a normal score drop."
        },
        {
          title: "What to verify first",
          body: "Check whether you have open accounts in your own name, whether they are actively updating, how recently they opened, and whether any bureau mismatch, freeze, fraud alert, or negative item is interfering."
        },
        {
          title: "What not to assume",
          body: "Do not assume being an authorized user alone will always create a strong score, and do not assume an old closed account is enough to keep the file scoreable."
        },
        {
          title: "Practical direction",
          body: "The next step is to confirm whether the issue is no file, too new a file, inactive history, negative interference, or a reporting/mismatch problem."
        }
      ],
      "First move: confirm whether active accounts are currently reporting in your own name."
    );
  }

  return pack(
    baseReason(),
    [
      {
        title: "What this may mean",
        body: baseReason()
      },
      {
        title: "Why it matters",
        body: "This signal was compared against the other possible causes so you can focus on the issue most likely to explain the score behavior first."
      },
      {
        title: "What to check first",
        body: "Review the current credit report data, compare it to what recently changed, and verify dates, balances, account status, and whether anything unfamiliar appears."
      },
      {
        title: "What not to assume",
        body: "Do not assume the first visible issue is automatically the true driver. Score changes are often caused by timing, reporting, balances, new activity, or negative items working together."
      }
    ],
    "First move: verify the exact data currently reporting before taking action."
  );
}

function normalizeBackendState(payload, currentState) {
  const nextState = Object.assign({}, currentState || {});
  const data = payload || {};
  const explanation = data.explanation || {};
  const diagnosis = data.diagnosis || {};
  const wow = data.wow || {};
  const rows = data.signalRows || [];

  if (data.state && typeof data.state === "object") {
    Object.assign(nextState, data.state);
  }

  if (data.answers && typeof data.answers === "object") {
    nextState.answers = Object.assign({}, nextState.answers || {}, data.answers);
  }

  if (data.currentQuestionId) nextState.currentQuestionId = data.currentQuestionId;
  if (data.nextQuestionId) nextState.currentQuestionId = data.nextQuestionId;
  if (data.questionId) nextState.currentQuestionId = data.questionId;

  [data.question, data.currentQuestion, data.nextQuestion].forEach(function (q) {
    if (q && q.id) {
      QUESTIONS[q.id] = q;
      nextState.currentQuestionId = q.id;
    }
  });

  if (typeof data.complete === "boolean") nextState.complete = data.complete;
  if (typeof data.done === "boolean") nextState.complete = data.done;

  if (data.diagnosis) {
    nextState.result = {
      primaryDiagnosis: diagnosis.key || DIAGNOSES.INVESTIGATION_NEEDED,
      confidence: diagnosis.confidence || "low",
      optimizeIndex: diagnosis.optimizeIndex || 54,
      grade: diagnosis.grade || "orange",

      whatYouToldUs: explanation.whatYouToldUs || "",
      whySystemFlagged: explanation.whyFlagged || "",
      whyThisWon: explanation.whyThisWon || "",
      whatToCheckNext: explanation.whatToCheckNext || "",

      wowReassurance: wow.reassurance || "",
      wowMessage: wow.message || "",

      signalTable: rows.reduce(function (acc, row) {
        acc[row.key] = {
          status: row.status || "orange",
          label: row.label || "Review item",
          reason: row.summary || "",
          detail: row.detail || "",
          score: row.score || 0,
          priority: row.priority || 0,
          rankTag: row.rankTag || ""
        };
        return acc;
      }, {}),

      secondaryDiagnoses: []
    };

    nextState.complete = true;
  }

  return nextState;
}

class CreditEngine {
  constructor() {
    this.state = deepClone(DEFAULT_STATE);
    this.state.loading = false;
    this.state.error = "";

    // ==========================================
    // RESTORE PURCHASED RESULT AFTER STRIPE
    // ==========================================
    try {
      const params = new URLSearchParams(global.location && global.location.search ? global.location.search : "");
      const unlocked = params.get("unlocked");

      if (unlocked === "1") {
        const savedStateRaw =
          localStorage.getItem("co_saved_state") ||
          sessionStorage.getItem("co_saved_state") ||
          sessionStorage.getItem("co_exec_last_result");

        const savedResultRaw =
          localStorage.getItem("co_saved_result") ||
          sessionStorage.getItem("co_saved_result");

        if (savedStateRaw) {
          const parsedState = JSON.parse(savedStateRaw);
          const parsedResult = savedResultRaw
            ? JSON.parse(savedResultRaw)
            : (parsedState && parsedState.result ? parsedState.result : null);

          this.state = Object.assign(
            {},
            deepClone(DEFAULT_STATE),
            parsedState || {}
          );

          if (parsedResult) {
            this.state.result = parsedResult;
          }

          this.state.complete = true;
          this.state.currentQuestionId = null;
          this.state.loading = false;
          this.state.error = "";
        }
      }
    } catch (e) {
      console.log("Credit Optimize restore failed", e);
    }
  }

  reset() {
    this.state = deepClone(DEFAULT_STATE);
    this.state.sessionId = uid();
    this.state.loading = false;
    this.state.error = "";
    return this;
  }

  getState() {
    return deepClone(this.state);
  }

  getCurrentQuestion() {
    if (this.state.complete) return null;
    return QUESTIONS[this.state.currentQuestionId] || QUESTIONS[QUESTION_IDS.ROOT_ISSUE] || null;
  }

  setLocalAnswer(questionId, value) {
    this.state.answers[questionId] = value;
    if (!this.state.asked.includes(questionId)) this.state.asked.push(questionId);
    this.state[questionId] = value;
  }

  async answerCurrent(value) {
    const question = this.getCurrentQuestion();
    if (!question || this.state.loading) return this.state;

    this.setLocalAnswer(question.id, value);
    this.state.loading = true;
    this.state.error = "";

    try {
      const response = await fetch(CO_DIAGNOSE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: this.state.sessionId,
          questionId: question.id,
          value: value,
          answers: this.state.answers,
          state: this.state
        })
      });

      if (!response.ok) {
        throw new Error("Diagnosis API returned " + response.status);
      }

      const data = await response.json();
      const refresh = this.state.onUiRefresh;

      this.state = normalizeBackendState(data, this.state);
      this.state.onUiRefresh = refresh;

    } catch (err) {
      console.error("Credit Optimize diagnosis API error:", err);
      this.state.error = "We could not complete the diagnosis. Please refresh and try again.";
    } finally {
      this.state.loading = false;
    }

    return this.state;
  }
}

  function buildPrimaryDiagnosisCard(result) {
    const primary = result.primaryDiagnosis;
    const confidence = labelForConfidence(result.confidence);

    return {
      title: diagnosisTitle(primary),
      confidence: confidence,
      score: result.optimizeIndex || 54,
      grade: diagnosisGrade(primary)
    };
  }

  function getScoreMeaning(score) {
    score = Number(score || 0);
    if (score >= 78) return "Strong structure";
    if (score >= 64) return "Fair structure — watch key issues";
    if (score >= 50) return "Moderate structure — needs improvement";
    return "High pressure — needs attention";
  }

function buildSignalRows(result, state) {
  const s = state || {};
  const rows = [];

  Object.keys(SIGNAL_LABELS).forEach(function (signalKey) {
    const signal = result.signalTable[signalKey];
    if (!signal) return;

    let statusText = "Watch";
    if (signal.status === "red") statusText = "Alert";
    if (signal.status === "orange") statusText = "Watch";
    if (signal.status === "green") statusText = "Stable";

    const rankTag = signal.rankTag || "";
    if (rankTag === "main") statusText = "Main issue";
    if (rankTag === "secondary") statusText = "Also affecting it";
    if (rankTag === "tertiary") statusText = "Worth checking";

    rows.push({
      key: signalKey,
      status: signal.status || "orange",
      statusText: statusText,
      rankTag: rankTag,
      rankTagText: getRankTagText(rankTag),
      label: getSignalDisplayLabel(signalKey, s, signal.label || SIGNAL_LABELS[signalKey]),
      summary: signal.reason || "",
      score: signal.score || 0,
      priority: signal.priority || 0,
      width: clamp(Math.round(((signal.score || 0) / 42) * 100), 10, 100)
    });
  });

  rows.sort(function (a, b) {
    const rankOrder = { main: 4, secondary: 3, tertiary: 2, "": 1 };
    const statusOrder = { red: 3, orange: 2, green: 1 };

    if (rankOrder[b.rankTag] !== rankOrder[a.rankTag]) {
      return rankOrder[b.rankTag] - rankOrder[a.rankTag];
    }

    if (statusOrder[b.status] !== statusOrder[a.status]) {
      return statusOrder[b.status] - statusOrder[a.status];
    }

    return (b.score || 0) - (a.score || 0);
  });

  return rows.slice(0, 6);
}

function renderPrimaryDiagnosisCard(result) {
  const card = buildPrimaryDiagnosisCard(result);

  return h("section", {
    class: "co-result-primary " + getPrimaryGradeClass(card.grade)
  }, [
    h("div", { class: "co-result-primary-top" }, [
      h("div", null, [
        h("p", { class: "co-kicker" }, ["Diagnosis"]),
        h("h2", { class: "co-result-primary-title" }, [card.title])
      ]),
      card.confidence ? h("div", { class: "co-result-badge" }, [card.confidence]) : null
    ]),

    h("div", { class: "co-result-score-wrap" }, [
      h("div", {
        class: "co-result-score-number",
        "data-co-score-target": String(card.score)
      }, ["0"]),
      h("div", { class: "co-result-score-label" }, ["Credit Structure Score"]),
      h("div", { class: "co-result-score-sub" }, [getScoreMeaning(card.score)])
    ])
  ]);
}

 function renderSignalRow(row, state) {
  const expandedId = state.expandedSignalKey || null;
  const isExpanded = expandedId === row.key;
  const detail = buildDetailedExplanation(row.key, state);

  return h("div", { class: "co-result-row" }, [
    h("button", {
      class: "co-result-row-button",
      type: "button",
      onclick: function () {
        state.expandedSignalKey = isExpanded ? null : row.key;
        if (typeof state.onUiRefresh === "function") state.onUiRefresh();
      }
    }, [
      h("div", {
        class: "co-result-row-status " + getStatusClass(row.status)
      }, [getStatusIcon(row.status)]),
      h("div", { class: "co-result-row-main" }, [
        h("div", { class: "co-result-row-topline" }, [
          h("p", { class: "co-result-row-label" }, [row.label]),
          row.rankTagText ? h("span", { class: "co-result-rank-pill co-rank-" + row.rankTag }, [row.rankTagText]) : null
        ]),
        h("div", { class: "co-result-row-bar" }, [
          h("span", { class: "co-result-row-bar-fill", style: { width: String(row.width || 12) + "%" } })
        ])
      ]),
      h("div", { class: "co-result-row-right" }, [
        h("span", { class: "co-result-row-status-text" }, [row.statusText]),
        h("span", { class: "co-result-row-arrow" }, [isExpanded ? "−" : "›"])
      ])
    ]),
    isExpanded && detail ? h("div", { class: "co-result-row-detail" }, [
      h("p", { class: "co-detail-body" }, [detail.body]),
      detail.sections && detail.sections.length
        ? h("div", { class: "co-deep-diagnostic-module" }, detail.sections.map(function(section) {
            return h("div", { class: "co-deep-diagnostic-block" }, [
              h("p", { class: "co-detail-action" }, [section.title]),
              h("p", { class: "co-detail-body" }, [section.body])
            ]);
          }))
        : null,
      detail.why
        ? h("p", { class: "co-detail-why" }, [detail.why])
        : null,
      detail.action
        ? h("p", { class: "co-detail-action" }, ["→ " + detail.action])
        : null
    ]) : null
  ]);
}

function renderSignalTable(result, state) {
  const rows = buildSignalRows(result, state);

  return h("section", { class: "co-result-table-wrap" }, [
    h("div", { class: "co-result-table-head" }, [
      h("span", null, ["Signal"]),
      h("span", null, ["Status"])
    ]),
    h("div", { class: "co-result-table" }, rows.map(function (row) {
      return renderSignalRow(row, state);
    }))
  ]);
}

  function renderMetaCards() {
    return null;
  }

  function renderInsightLists() {
    return null;
  }

  // ---------------------------------------------------------
  // RENDER / CINEMATIC / APP CONTINUE BELOW
  // --------------------------------------------------------- 
    // ---------------------------------------------------------
  // RENDER HELPERS / CINEMATIC / SCORE ANIMATION / MAIN UI
  // ---------------------------------------------------------

  function animateScoreCount(root) {
    if (!root) return;

    const el = root.querySelector("[data-co-score-target]");
    if (!el) return;

    const target = parseInt(el.getAttribute("data-co-score-target"), 10) || 0;
    const duration = isMobileViewport() ? 1250 : 1550;
    const start = performance.now();

    function frame(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);
      el.textContent = String(current);

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = String(target);
      }
    }

    requestAnimationFrame(frame);
  }
function getSoftTimeText(state) {
  const asked = state && Array.isArray(state.asked) ? state.asked.length : 0;

  if (asked <= 0) return "About 60 seconds";
  if (asked === 1) return "Less than a minute";
  if (asked === 2) return "A few quick checks left";
  if (asked === 3) return "Halfway through";
  if (asked >= 4) return "Almost done";

  return "Quick guided review";
}

function getQuestionKicker(state, question) {
  const asked = state && Array.isArray(state.asked) ? state.asked.length : 0;
  const group = question && question.group ? question.group : "";

  if (asked === 0) return "Understand what may have changed";
  if (group === "negative") return "Checking stronger score signals";
  if (group === "timing") return "Checking reporting timing";
  if (group === "utilization") return "Checking balance pressure";
  if (group === "new_credit") return "Checking new credit activity";
  if (group === "limit") return "Checking available credit changes";
  if (group === "no_score") return "Checking score visibility";
  if (asked >= 4) return "Almost ready";

  return "Narrowing the pattern";
}

function getQuestionIntro(state, question) {
  const asked = state && Array.isArray(state.asked) ? state.asked.length : 0;
  const qid = question && question.id ? question.id : "";

  if (asked === 0 || qid === QUESTION_IDS.ROOT_ISSUE) {
    return "This quick review looks for balance pressure, reporting timing, new credit activity, hidden negatives, and account changes that may explain what you’re seeing.";
  }

  if (qid === QUESTION_IDS.RECENT_CHANGE) {
    return "Most score changes start with a recent event. Choose the closest match, even if you’re not completely sure.";
  }

  if (qid === QUESTION_IDS.PAYDOWN_TYPE) {
    return "This helps separate a real balance issue from a timing issue.";
  }

  if (qid === QUESTION_IDS.CARDS_WITH_BALANCES) {
    return "The number of cards showing balances can matter, not just the total amount owed.";
  }

  if (qid === QUESTION_IDS.HIGHEST_UTIL_BUCKET) {
    return "One card can sometimes carry most of the score pressure by itself.";
  }

  if (qid === QUESTION_IDS.USED_AGAIN_AFTER_PAYDOWN) {
    return "This is a common reason people feel like they did the right thing but the score still reacted badly.";
  }

  if (qid === QUESTION_IDS.PAID_BEFORE_REPORTING || qid === QUESTION_IDS.PAYMENTS_REFLECTED_YET || qid === QUESTION_IDS.KNOW_REPORTING_DATE) {
    return "Credit scores usually react to what was reported — not always what your bank app shows today.";
  }

  if (qid === QUESTION_IDS.NEGATIVE_TYPE || qid === QUESTION_IDS.RECOGNIZE_NEGATIVE) {
    return "A negative item can outweigh almost everything else, so we check this carefully.";
  }

  if (qid === QUESTION_IDS.NEW_ACCOUNT_TYPE || qid === QUESTION_IDS.NEW_ACCOUNT_TIMING || qid === QUESTION_IDS.SCORE_DROP_AFTER_NEW_CREDIT) {
    return "New credit can create a short-term reaction before it has time to help.";
  }

  if (qid === QUESTION_IDS.LIMIT_DECREASE_OR_CLOSURE_CONFIRMED) {
    return "A limit cut or closure can make the same balances look heavier without new spending.";
  }

  if (qid === QUESTION_IDS.ANY_UNKNOWN_ACTIVITY) {
    return "Before assuming this is normal score movement, it helps to rule out anything unfamiliar.";
  }

  if (qid === QUESTION_IDS.NO_SCORE_SITUATION) {
    return "A missing score is usually a file-structure issue, not the same thing as a normal score drop.";
  }

  return "A few quick answers help the system narrow the most likely pattern.";
}

function buildQuestionTrustStrip(state, question) {
  const asked = state && Array.isArray(state.asked) ? state.asked.length : 0;

  if (asked > 0) {
    return h("div", { class: "co-soft-progress-row" }, [
      h("span", { class: "co-soft-progress-dot" }),
      h("span", null, [getSoftTimeText(state)])
    ]);
  }

  return h("div", { class: "co-trust-strip" }, [
    h("span", null, ["No SSN"]),
    h("span", null, ["No account linking"]),
    h("span", null, ["Educational review"]),
    h("span", null, [getSoftTimeText(state)])
  ]);
}

  function buildQuestionScreen(app) {
    const state = app.engine.state;
    const question = app.engine.getCurrentQuestion() || QUESTIONS[QUESTION_IDS.ROOT_ISSUE];
    const questionTitle = pickQuestionTitle(question);
    const microBridge = getMicroBridge(state, question ? question.id : null);
    const asked = state && Array.isArray(state.asked) ? state.asked.length : 0;
    const isFirstQuestion = asked === 0 || (question && question.id === QUESTION_IDS.ROOT_ISSUE);

    return h("div", { class: "co-screen co-question-screen" + (isFirstQuestion ? " co-first-question-screen" : "") }, [
      h("div", { class: "co-card co-q-card co-q-card-emotional" }, [
        app.canGoBack()
          ? h("button", {
              class: "co-back-btn",
              type: "button",
              "aria-label": "Go back to the previous question",
              onclick: function () {
                app.goBack();
              }
            }, ["← Back"])
          : null,

        h("div", { class: "co-question-wrap co-question-wrap-emotional" }, [
          h("div", { class: "co-question-topline" }, [
            h("p", { class: "co-kicker co-warm-kicker" }, [getQuestionKicker(state, question)]),
            h("div", { class: "co-time-pill" }, [getSoftTimeText(state)])
          ]),

          isFirstQuestion
            ? h("div", { class: "co-first-orientation" }, [
                h("h1", { class: "co-q-title co-q-title-hero" }, ["Let’s look at what may be affecting your credit."]),
                h("p", { class: "co-q-intro co-q-intro-hero" }, [getQuestionIntro(state, question)]),
                buildQuestionTrustStrip(state, question)
              ])
            : h("div", { class: "co-question-copy-block" }, [
                h("h1", { class: "co-q-title" }, [questionTitle || "Question"]),
                h("p", { class: "co-q-intro" }, [getQuestionIntro(state, question)]),
                microBridge
                  ? h("p", { class: "co-micro-bridge co-micro-bridge-warm" }, [microBridge])
                  : null,
                buildQuestionTrustStrip(state, question)
              ]),

          isFirstQuestion
            ? h("div", { class: "co-first-question-label" }, [
                h("span", null, ["Start with what you noticed:"])
              ])
            : null,

          h("div", { class: "co-option-grid co-option-grid-emotional" },
            ((question && question.options) || []).map(function (option, index) {
              return h("button", {
                class: "co-option-btn co-option-btn-emotional",
                type: "button",
                "data-option-index": String(index + 1),
                onclick: function () {
                  app.handleAnswer(option.value);
                }
              }, [
                h("span", { class: "co-option-number" }, [String(index + 1)]),
                h("span", { class: "co-option-label" }, [pickOptionLabel(option)]),
                h("span", { class: "co-option-arrow" }, ["›"])
              ]);
            })
          )
        ])
      ])
    ]);
  }

  function buildCinematicScreen(app) {
    const state = app.engine.getState();
    const lines = buildCinematicLines(state);
    const progressState = getDiagnosisProgressState(state, state.result || {});
    const bars = progressState.bars || [];

    return h("div", {
      class: "co-screen co-screen-center co-cinematic-shell"
    }, [
      h("div", { class: "co-card co-cine-card" }, [
        h("div", { class: "co-cine-topline" }, [
          h("span", { class: "co-cine-pulse", "data-co-cine-pulse": "1" }),
          h("span", { class: "co-cine-status", "data-co-cine-status": "1" }, [
            "Analyzing"
          ])
        ]),
        h("h2", { class: "co-cine-title" }, ["Building your diagnosis"]),
        h("p", { class: "co-cine-copy" }, [
          "We’re tracking multiple possible causes, narrowing the strongest signal, and checking whether secondary issues matter too."
        ]),
        h("div", { class: "co-cine-progress" }, [
          h("div", {
            class: "co-cine-progress-fill",
            style: { width: String(progressState.progress || 24) + "%" },
            "data-co-cine-progress": "1"
          })
        ]),
        h("div", { class: "co-cine-signal-bars" }, bars.map(function (bar) {
          return h("div", { class: "co-cine-signal-row" }, [
            h("span", { class: "co-cine-signal-label" }, [bar.label]),
            h("span", { class: "co-cine-signal-track" }, [
              h("span", { class: "co-cine-signal-fill", style: { width: String(bar.width || 12) + "%" } })
            ])
          ]);
        })),
        h("div", { class: "co-cine-rows", "data-co-cine-rows": "1" }, lines.map(function (line) {
          return h("div", { class: "co-cine-row" }, [
            h("span", { class: "co-cine-dot" }),
            h("span", null, [line])
          ]);
        }))
      ])
    ]);
  }


  function getAppConfigValue(key, fallback) {
    try {
      const configEl = document.getElementById("coAppConfig");
      if (!configEl) return fallback;
      const config = JSON.parse(configEl.textContent || "{}");
      return Object.prototype.hasOwnProperty.call(config, key) ? config[key] : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function getCheckoutUrl() {
    try {
      const configEl = document.getElementById("coAppConfig");
      const config = configEl ? JSON.parse(configEl.textContent || "{}") : {};
      if (config.checkoutUrl && String(config.checkoutUrl).trim()) {
        return String(config.checkoutUrl).trim();
      }
    } catch (e) {}

    // Fallback keeps the current test behavior if no checkout URL is provided in Liquid.
    return "https://creditoptimizehub.com/pages/purchase-success";
  }

  function getPriceLabel() {
    return "$27";
  }

  function coTrackEvent(eventName, pageName, extra) {
    extra = extra || {};

    const payload = Object.assign(
      {},
      extra || {},
      {
        page_name: pageName || "credit_diagnostic",
        tracking_source: "credit_optimize_ui_shell"
      }
    );

    // Primary dashboard/Supabase tracker injected from the Shopify Liquid page.
    // This is what powers the Vercel Journey dashboard.
    try {
      if (typeof global.coTrackEvent === "function") {
        global.coTrackEvent(eventName, payload);
      } else if (typeof global.trackEvent === "function") {
        global.trackEvent(eventName, payload);
      } else if (typeof global.CO_TRACK === "function") {
        global.CO_TRACK(eventName, pageName || "credit_diagnostic", payload);
      }
    } catch (e) {
      console.log("CO dashboard tracking bridge error", e);
    }

    // Optional analytics mirrors. These do not power the internal dashboard.
    try {
      if (typeof global.gtag === "function") {
        global.gtag("event", eventName, payload);
      }
    } catch (e) {}

    try {
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (e) {}

    try {
      console.log("CO UI EVENT:", eventName, payload);
    } catch (e) {}
  }

  function getTrackingParams() {
    try {
      const params = new URLSearchParams(global.location && global.location.search ? global.location.search : "");
      return {
        page_path: global.location ? global.location.pathname : "",
        page_location: global.location ? global.location.href : "",
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        utm_term: params.get("utm_term"),
        hook: params.get("h"),
        h: params.get("h"),
        device_width: getViewportWidth()
      };
    } catch (e) {
      return {};
    }
  }

  function getTrackingResultMeta(state) {
    const result = (state && state.result) || {};
    return Object.assign(
      {
        session_id: state && state.sessionId ? state.sessionId : null,
        primary_diagnosis: result.primaryDiagnosis || null,
        confidence: result.confidence || null,
        optimize_index: result.optimizeIndex || null,
        questions_answered: state && state.asked ? state.asked.length : 0,
        complete: !!(state && state.complete)
      },
      getTrackingBranchMeta(state)
    );
  }

  function getQuestionStepNumber(state, question, mode) {
    const asked = state && Array.isArray(state.asked) ? state.asked : [];
    const questionId = question && question.id ? question.id : (state && state.currentQuestionId ? state.currentQuestionId : null);
    const existingIndex = questionId ? asked.indexOf(questionId) : -1;

    if (existingIndex >= 0) return existingIndex + 1;
    if (mode === "answered") return asked.length + 1;
    return asked.length + 1;
  }

  function getBranchFromState(state, question) {
    state = state || {};
    question = question || {};

    const answers = state.answers || {};
    const root = state.rootIssue || answers[QUESTION_IDS.ROOT_ISSUE] || null;
    const recent = state.recentChange || answers[QUESTION_IDS.RECENT_CHANGE] || null;
    const result = state.result || {};
    const qGroup = question.group || null;

    if (root === ROOT_ISSUES.NO_SCORE || qGroup === "no_score") return "no_score";
    if (root === ROOT_ISSUES.NEW_NEGATIVE_FOUND || recent === RECENT_CHANGE.MISSED_PAYMENT_OR_NEGATIVE || qGroup === "negative") return "negative";
    if (recent === RECENT_CHANGE.LIMIT_DECREASE_OR_CARD_CLOSED || qGroup === "limit") return "limit_or_closure";
    if (recent === RECENT_CHANGE.OPENED_NEW_ACCOUNT || qGroup === "new_credit") return "new_credit";
    if (root === ROOT_ISSUES.SCORE_DROPPED_AFTER_PAYDOWN || recent === RECENT_CHANGE.PAID_DOWN_BALANCES || qGroup === "utilization" || qGroup === "timing") return "utilization_timing";
    if (qGroup === "file_strength") return "file_strength";
    if (qGroup === "fraud") return "unknown_activity";
    if (result.primaryDiagnosis) return result.primaryDiagnosis;

    return "unassigned";
  }

  function getPrimaryIssueFromState(state, question) {
    state = state || {};
    const result = state.result || {};
    if (result.primaryDiagnosis) return result.primaryDiagnosis;
    return getBranchFromState(state, question);
  }

  function getSecondaryIssuesFromState(state) {
    state = state || {};
    const result = state.result || {};
    const issues = [];

    if (Array.isArray(result.secondaryDiagnoses)) {
      result.secondaryDiagnoses.forEach(function (item) {
        if (typeof item === "string") pushUnique(issues, item);
        else if (item && item.key) pushUnique(issues, item.key);
      });
    }

    const table = result.signalTable || {};
    Object.keys(table).forEach(function (key) {
      const signal = table[key] || {};
      if (signal.status === "red" || signal.status === "orange") pushUnique(issues, key);
    });

    return issues;
  }

  function getPathTaken(state) {
    state = state || {};
    const answers = state.answers || {};
    const asked = Array.isArray(state.asked) ? state.asked : [];

    return asked.map(function (questionId, index) {
      const question = QUESTIONS[questionId] || {};
      return {
        q_number: index + 1,
        question_id: questionId,
        question_group: question.group || null,
        answer_value: Object.prototype.hasOwnProperty.call(answers, questionId) ? answers[questionId] : null
      };
    });
  }

  function getTrackingBranchMeta(state, question) {
    const secondaryIssues = getSecondaryIssuesFromState(state);

    return {
      branch: getBranchFromState(state, question),
      branch_type: getBranchFromState(state, question),
      primary_issue: getPrimaryIssueFromState(state, question),
      secondary_issue: secondaryIssues.length ? secondaryIssues[0] : null,
      secondary_issues: secondaryIssues,
      path_taken: getPathTaken(state)
    };
  }

  function getTrackingQuestionMeta(state, question, extra) {
    extra = extra || {};
    const mode = extra.event_action === "answered" || String(extra.funnel_step || "").indexOf("answered") >= 0 ? "answered" : "viewed";
    const questionStep = extra.question_step || getQuestionStepNumber(state, question, mode);

    return Object.assign(
      {},
      getTrackingParams(),
      getTrackingBranchMeta(state, question),
      {
        session_id: state && state.sessionId ? state.sessionId : null,
        funnel_step: extra.funnel_step || "diagnostic_question",
        question_id: question && question.id ? question.id : (state && state.currentQuestionId ? state.currentQuestionId : null),
        question_key: question && question.id ? question.id : (state && state.currentQuestionId ? state.currentQuestionId : null),
        question_title: question ? pickQuestionTitle(question) : null,
        question_group: question && question.group ? question.group : null,
        question_step: questionStep,
        q_number: questionStep,
        questions_answered: state && state.asked ? state.asked.length : 0,
        product: "credit_diagnostic",
        price: 27,
        free_start: true
      },
      extra
    );
  }

  function trackQuestionViewed(app, state, question) {
    state = state || {};
    question = question || {};

    const qNumber = getQuestionStepNumber(state, question, "viewed");
    const payload = getTrackingQuestionMeta(state, question, {
      event_action: "viewed",
      funnel_step: "q" + qNumber + "_viewed",
      q_number: qNumber
    });

    coTrackOnce(app, "question_view_" + (question.id || "unknown") + "_" + qNumber, "question_view", "diagnostic_question", payload);
    coTrackOnce(app, "q_viewed_" + (question.id || "unknown") + "_" + qNumber, "q_viewed", "diagnostic_question", payload);

    if (qNumber >= 1 && qNumber <= 8) {
      coTrackOnce(app, "q" + qNumber + "_viewed_" + (question.id || "unknown"), "q" + qNumber + "_viewed", "diagnostic_question", payload);
    }
  }

  function trackQuestionAnswered(state, question, value, stateAfter) {
    state = state || {};
    question = question || {};
    stateAfter = stateAfter || {};

    const qNumber = getQuestionStepNumber(state, question, "answered");
    const nextQuestionId = stateAfter.currentQuestionId || null;
    const nextQuestion = nextQuestionId ? QUESTIONS[nextQuestionId] || null : null;
    const payload = getTrackingQuestionMeta(state, question, {
      event_action: "answered",
      funnel_step: "q" + qNumber + "_answered",
      q_number: qNumber,
      answer_value: value,
      answer_key: value,
      answer_label: (function () {
        try {
          const match = ((question && question.options) || []).find(function (option) {
            return option && option.value === value;
          });
          return match ? pickOptionLabel(match) : String(value || "");
        } catch (e) {
          return String(value || "");
        }
      })(),
      next_question_id: nextQuestionId,
      next_question_key: nextQuestionId,
      next_question_group: nextQuestion && nextQuestion.group ? nextQuestion.group : null,
      completed_after_answer: !!stateAfter.complete
    });

    coTrackEvent("question_answered", "diagnostic_question", payload);
    coTrackEvent("q_answered", "diagnostic_question", payload);

    if (qNumber >= 1 && qNumber <= 8) {
      coTrackEvent("q" + qNumber + "_answered", "diagnostic_question", payload);
    }
  }

  function trackPreWowComplete(state) {
    state = state || {};
    const payload = Object.assign(
      {},
      getTrackingParams(),
      getTrackingResultMeta(state),
      {
        funnel_step: "pre_wow_complete",
        product: "credit_diagnostic",
        price: 27,
        free_start: true,
        free_diagnostic_complete: true,
        questions_answered: state && state.asked ? state.asked.length : 0,
        path_taken: getPathTaken(state)
      }
    );

    coTrackEvent("pre_wow_complete", "diagnostic_question", payload);
  }

  function coTrackOnce(app, key, eventName, pageName, extra) {
    if (!app) {
      coTrackEvent(eventName, pageName, extra || {});
      return;
    }

    app._trackedEvents = app._trackedEvents || {};

    if (app._trackedEvents[key]) return;
    app._trackedEvents[key] = true;

    coTrackEvent(eventName, pageName, extra || {});
  }


  function getFreeSignalRows(result) {
    const rows = buildSignalRows(result || { signalTable: {} }, {});
    if (rows && rows.length) return rows.slice(0, 3);

    return [
      {
        key: "balance",
        label: "Score-pressure signal",
        status: "orange",
        statusText: "Detected",
        summary: "The system found a pattern worth reviewing.",
        width: 78
      },
      {
        key: "timing",
        label: "Reporting/timing signal",
        status: "orange",
        statusText: "Possible",
        summary: "The timing may not match what you expected.",
        width: 62
      },
      {
        key: "negative",
        label: "Negative-activity check",
        status: "green",
        statusText: "Checked",
        summary: "This was compared against stronger negative signals.",
        width: 42
      }
    ];
  }

  function buildFreeSignalPreview(result) {
    const rows = getFreeSignalRows(result);

    return h("div", { class: "co-free-signal-preview" }, [
      h("div", { class: "co-free-signal-head" }, [
        h("span", null, ["Free signal preview"]),
        h("span", null, ["Snapshot"])
      ]),
      h("div", { class: "co-free-signal-list" }, rows.map(function (row, index) {
        const label = row.label || "Review signal";
        const status = row.status || (index === 0 ? "orange" : "green");
        const statusText = row.rankTagText || row.statusText || (index === 0 ? "Detected" : "Checked");

        return h("div", { class: "co-free-signal-row" }, [
          h("div", { class: "co-free-signal-icon " + getStatusClass(status) }, [getStatusIcon(status)]),
          h("div", { class: "co-free-signal-main" }, [
            h("div", { class: "co-free-signal-label" }, [label]),
            h("div", { class: "co-free-signal-bar" }, [
              h("span", { style: { width: String(row.width || 55) + "%" } })
            ])
          ]),
          h("div", { class: "co-free-signal-status" }, [statusText])
        ]);
      }))
    ]);
  }

  function buildLockedPreviewCard(title, body) {
    return h("div", { class: "co-locked-preview-card" }, [
      h("div", { class: "co-locked-preview-lock" }, ["🔒"]),
      h("div", null, [
        h("strong", null, [title]),
        h("p", null, [body])
      ])
    ]);
  }

  function buildWowScreen(app) {
    const result = app.engine.state.result || {};
    const freeMessage =
      result.wowMessage ||
      result.explanation ||
      result.summary ||
      "Your answers point to a credit signal worth reviewing more closely.";

    return h("div", { class: "co-screen co-screen-center co-wow-shell co-wow-free-result", id: "coWowScreen" }, [
      h("div", { class: "co-wow-stage" }, [
        h("div", { class: "co-wow-stage-inner" }, [
          h("div", { class: "co-card co-wow-card co-wow-card-cliff", "data-co-wow-card": "1" }, [
            h("p", { class: "co-wow-reassurance", "data-co-wow-reassurance": "1" }, ["Free diagnostic complete"]),
            h("h2", { class: "co-wow-text", "data-co-wow-headline": "1" }, [
              "We found a likely credit signal."
            ]),
            h("p", { class: "co-wow-cliff-copy", "data-co-wow-copy": "1" }, [
              "Your free diagnostic identified the strongest pattern showing in your answers. The full diagnostic unlocks the ranked explanation, why it matters, and what to check first."
            ]),
            h("p", { class: "co-wow-detected-copy", "data-co-wow-detected": "1" }, [
              freeMessage
            ]),
            buildFreeSignalPreview(result),

            h("div", { class: "co-locked-preview-grid co-wow-inline-locks", "data-co-wow-strip": "1" }, [
              buildLockedPreviewCard(
                "Ranked primary cause",
                "See which signal ranked strongest instead of guessing between timing, balances, negatives, or file structure."
              ),
              buildLockedPreviewCard(
                "Why this was flagged",
                "See the plain-English reasoning behind the diagnosis based on your answers."
              ),
              buildLockedPreviewCard(
                "What to check first",
                "See the next review steps so you know where to look before making another move."
              )
            ]),

            h("button", {
              class: "co-wow-btn",
              type: "button",
              onclick: function () { app.finishWow(); },
              "data-co-wow-btn": "1"
            }, ["Unlock Full Diagnostic — " + getPriceLabel()])
          ])
        ])
      ])
    ]);
  }

  function buildInsightSection(title, body) {
    return h("section", { class: "co-result-insight-card" }, [
      h("p", { class: "co-kicker" }, [title]),
      h("p", { class: "co-result-insight-body" }, [body || ""])
    ]);
  }


function buildPaymentGateScreen(app) {
  const state = app.engine.state;
  const result = state.result || {};
  const priceLabel = getPriceLabel();
  const checkoutUrl = getCheckoutUrl();

  return h("div", { class: "co-screen co-screen-center co-payment-gate-shell" }, [
    h("div", { class: "co-payment-gate-wrap" }, [
      h("div", { class: "co-card co-payment-gate-card" }, [
        h("p", { class: "co-kicker" }, ["Full diagnostic available"]),
        h("h1", { class: "co-payment-title" }, ["Want the deeper breakdown?"]),
        h("p", { class: "co-payment-sub" }, [
          "Your free scan found the likely signal. The full diagnostic shows the ranked cause, why the score may be reacting this way, and what to check first."
        ]),

        buildFreeSignalPreview(result),

        h("div", { class: "co-locked-preview-grid" }, [
          buildLockedPreviewCard(
            "Ranked primary cause",
            "See which signal appears strongest instead of guessing between timing, balances, negatives, or file structure."
          ),
          buildLockedPreviewCard(
            "Why this was flagged",
            "See the plain-English reasoning behind the diagnosis based on your answers."
          ),
          buildLockedPreviewCard(
            "What to check next",
            "See the next review steps so you know where to look first."
          )
        ]),

        h("button", {
          class: "co-payment-cta",
          type: "button",
          onclick: function () {
            coTrackEvent("checkout_click", "payment_gate", {
              funnel_step: "payment_gate_to_checkout",
              product: "credit_diagnostic",
              price: 27,
              checkout_url: checkoutUrl
            });

            try {
              const paidState = app.engine.state || {};
              const paidResult = paidState.result || {};

              sessionStorage.setItem("co_exec_unlock_pending", "1");
              sessionStorage.setItem("co_exec_last_result", JSON.stringify(paidState));
              sessionStorage.setItem("co_saved_state", JSON.stringify(paidState));
              sessionStorage.setItem("co_saved_result", JSON.stringify(paidResult));

              localStorage.setItem("co_saved_state", JSON.stringify(paidState));
              localStorage.setItem("co_saved_result", JSON.stringify(paidResult));
            } catch (e) {}

            window.location.href = checkoutUrl;
          }
        }, ["Unlock My Full Diagnostic — " + priceLabel]),

        h("div", { class: "co-payment-trust" }, [
          "No SSN • No account linking • Educational diagnostic only"
        ])
      ])
    ])
  ]);
}

function buildResultScreen(app) {
  const state = app.engine.state;
  const result = state.result;

  return h("div", { class: "co-screen" }, [
    h("div", { class: "co-result-wrap co-result-wrap-compact" }, [

      renderPrimaryDiagnosisCard(result),

      buildInsightSection(
        "What you told us",
        result.whatYouToldUs || result.whatWeFound || result.summary || ""
      ),

      buildInsightSection(
        "Why this is likely happening",
        result.whySystemFlagged || result.whyWeThinkThat || result.explanation || ""
      ),

      buildInsightSection(
        "Why this ranked first",
        result.whyThisWon || result.whatMakesThisDifferent || ""
      ),

      buildInsightSection(
        "What to check next",
        result.whatToCheckNext || result.whatMattersFirst || result.primaryAction || ""
      ),



      // signal table AFTER
      renderSignalTable(result, state),

      h("section", { class: "co-next-layer-card" }, [
        h("p", { class: "co-kicker" }, ["Next layer"]),
        h("h3", { class: "co-next-layer-title" }, ["Now you know the likely reason. Next is knowing what to check first."]),
        h("p", { class: "co-next-layer-body" }, ["The upcoming Execution Plan will show what to check first, what to avoid, and how to handle the timing correctly."])
      ])

    ])
  ]);
}


  function startWowSequence(root) {
    if (!root) return;

    const reassurance = root.querySelector('[data-co-wow-reassurance]');
    const headline = root.querySelector('[data-co-wow-headline]');
    const btn = root.querySelector('[data-co-wow-btn]');
    const card = root.querySelector('[data-co-wow-card]');
    if (card) card.classList.add('is-visible');

    [reassurance, headline, btn].forEach(function(el){
      if (el) el.classList.remove('is-visible');
    });

    setTimeout(function(){ if (reassurance) reassurance.classList.add('is-visible'); }, 160);
    setTimeout(function(){ if (headline) headline.classList.add('is-visible'); }, 460);
    setTimeout(function(){ if (btn) btn.classList.add('is-visible'); }, 780);
  }

  function startCinematicSequence(app, root) {
    const progressEl = root.querySelector("[data-co-cine-progress]");
    const rows = Array.prototype.slice.call(root.querySelectorAll(".co-cine-row"));
    const pulse = root.querySelector("[data-co-cine-pulse]");
    const status = root.querySelector("[data-co-cine-status]");

    let index = 0;

    function step() {
      rows.forEach(function (row, i) {
        row.classList.remove("active");
        if (i < index) row.classList.add("done");
        else row.classList.remove("done");
      });

      if (index < rows.length) {
        rows[index].classList.add("active");
      }

      const progress = Math.min(((index + 1) / rows.length) * 100, 100);
      if (progressEl) progressEl.style.width = progress + "%";

      index += 1;

      if (index <= rows.length) {
        setTimeout(step, 360);
      } else {
        if (pulse) {
          pulse.classList.add("co-cine-pulse-done");
        }
        if (status) {
          status.textContent = "Diagnosis ready";
        }
        setTimeout(function () {
          app.finishCinematic();
        }, 420);
      }
    }

    setTimeout(step, 180);
  }


  // ---------------------------------------------------------
  // PAGE LOCK / SCREEN MODE HELPERS
  // ---------------------------------------------------------

function shouldLockPageForMode(mode) {
  return false;
}

  function applyPageLock(mode) {
    const lock = shouldLockPageForMode(mode);
    try {
      document.documentElement.classList.toggle("co-page-locked", lock);
      document.body.classList.toggle("co-page-locked", lock);
      document.documentElement.classList.toggle("co-page-scrollable", !lock);
      document.body.classList.toggle("co-page-scrollable", !lock);
    } catch (e) {}
  }

  // ---------------------------------------------------------
  // APP CONTROLLER
  // ---------------------------------------------------------

  function CreditOptimizePaidApp(root) {
    this.root = root;
    this.engine = new CreditEngine();
    this.engine.state.onUiRefresh = this.render.bind(this);
    this._trackedEvents = {};

    try {
      const params = new URLSearchParams(global.location && global.location.search ? global.location.search : "");
      const unlocked = params.get("unlocked");
      this.mode = (unlocked === "1" && this.engine.state && this.engine.state.complete) ? "result" : "question";
    } catch (e) {
      this.mode = "question";
    }

    this.progressSteps = 8;
    this.setupSwipeBack();
  }

  CreditOptimizePaidApp.prototype.canGoBack = function () {
    const state = this.engine && this.engine.state ? this.engine.state : {};
    return this.mode === "question" && Array.isArray(state.asked) && state.asked.length > 0 && !state.loading;
  };

  CreditOptimizePaidApp.prototype.goBack = function () {
    const state = this.engine && this.engine.state ? this.engine.state : null;
    if (!state || !Array.isArray(state.asked) || state.asked.length === 0 || state.loading) return;

    const lastQuestionId = state.asked.pop();

    if (lastQuestionId) {
      if (state.answers && Object.prototype.hasOwnProperty.call(state.answers, lastQuestionId)) {
        delete state.answers[lastQuestionId];
      }
      if (Object.prototype.hasOwnProperty.call(state, lastQuestionId)) {
        delete state[lastQuestionId];
      }
      state.currentQuestionId = lastQuestionId;
    } else {
      state.currentQuestionId = QUESTION_IDS.ROOT_ISSUE;
    }

    state.complete = false;
    state.error = "";
    this.mode = "question";
    this.render();
  };

  CreditOptimizePaidApp.prototype.setupSwipeBack = function () {
    if (!this.root || this._swipeBackBound) return;
    this._swipeBackBound = true;

    let startX = 0;
    let startY = 0;
    let startTime = 0;

    this.root.addEventListener("touchstart", function (event) {
      if (!event.touches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
    }, { passive: true });

    this.root.addEventListener("touchend", function (event) {
      if (!event.changedTouches || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const elapsed = Date.now() - startTime;

      if (startX <= 90 && dx > 70 && Math.abs(dy) < 55 && elapsed < 700 && this.canGoBack()) {
        this.goBack();
      }
    }.bind(this), { passive: true });
  };

  CreditOptimizePaidApp.prototype.handleAnswer = async function (value) {
    const stateBefore = this.engine && this.engine.state ? deepClone(this.engine.state) : {};
    const questionBefore = this.engine.getCurrentQuestion ? this.engine.getCurrentQuestion() : null;

    await this.engine.answerCurrent(value);

    trackQuestionAnswered(stateBefore, questionBefore, value, this.engine.state);

    if (this.engine.state.complete) {
      trackPreWowComplete(this.engine.state);

      coTrackEvent("diagnostic_completed", "diagnostic_question", Object.assign(
        {},
        getTrackingParams(),
        getTrackingResultMeta(this.engine.state),
        {
          funnel_step: "diagnostic_completed",
          product: "credit_diagnostic",
          price: 27,
          free_start: true
        }
      ));

      this.mode = "wow";
      this.render();
      return;
    }

    this.mode = "question";
    this.render();
  };

  CreditOptimizePaidApp.prototype.finishCinematic = function () {
    this.mode = "wow";
    this.engine.state.cinematicSeen = true;
    this.render();
  };

  CreditOptimizePaidApp.prototype.finishWow = function () {
    this.engine.state.wowSeen = true;

    const checkoutUrl = getCheckoutUrl();

    coTrackEvent("unlock_click", "free_result", Object.assign(
      {},
      getTrackingParams(),
      getTrackingResultMeta(this.engine.state),
      {
        funnel_step: "free_result_unlock_click",
        product: "credit_diagnostic",
        price: 27,
        checkout_url: checkoutUrl,
        free_diagnostic_complete: true
      }
    ));

    coTrackEvent("checkout_click", "free_result", Object.assign(
      {},
      getTrackingParams(),
      getTrackingResultMeta(this.engine.state),
      {
        funnel_step: "free_result_to_checkout",
        product: "credit_diagnostic",
        price: 27,
        checkout_url: checkoutUrl,
        free_diagnostic_complete: true
      }
    ));

    try {
      const paidState = this.engine.state || {};
      const paidResult = paidState.result || {};

      sessionStorage.setItem("co_exec_unlock_pending", "1");
      sessionStorage.setItem("co_exec_last_result", JSON.stringify(paidState));
      sessionStorage.setItem("co_saved_state", JSON.stringify(paidState));
      sessionStorage.setItem("co_saved_result", JSON.stringify(paidResult));

      localStorage.setItem("co_saved_state", JSON.stringify(paidState));
      localStorage.setItem("co_saved_result", JSON.stringify(paidResult));
    } catch (e) {}

    window.location.href = checkoutUrl;
  };



  CreditOptimizePaidApp.prototype.getProgressPercent = function () {
    const askedCount = (this.engine.state.asked || []).length;
    const percent = (askedCount / this.progressSteps) * 100;
    return clamp(percent, 10, 100);
  };

  CreditOptimizePaidApp.prototype.renderShellTop = function () {
    const progress = (this.mode === "result") ? 100 : this.getProgressPercent();
    const state = this.engine && this.engine.state ? this.engine.state : {};
    const asked = Array.isArray(state.asked) ? state.asked.length : 0;

    return h("div", { class: "co-shell-top co-shell-top-warm" }, [
      h("div", { class: "co-shell-top-row" }, [
        h("div", { class: "co-brand-stack" }, [
          h("div", { class: "co-brand-mark" }, ["CO"]),
          h("div", null, [
            h("div", { class: "co-brand" }, ["Credit Optimize"]),
            h("div", { class: "co-sub" }, ["Credit clarity review"])
          ])
        ]),
        h("div", { class: "co-shell-badge co-shell-badge-warm" }, [
          (this.mode === "result") ? "Complete" : (asked <= 0 ? "Start here" : getSoftTimeText(state))
        ])
      ]),
      h("div", { class: "co-progress-wrap co-progress-wrap-warm" }, [
        h("div", { class: "co-progress-track" }, [
          h("div", {
            class: "co-progress-fill",
            style: { width: progress + "%" }
          })
        ])
      ])
    ]);
  };

  CreditOptimizePaidApp.prototype.render = function () {
    injectBaseStyles();
    injectEnhancedStyles();
    injectWarmQuestionStyles();
    injectFinalLaunchStyles();
    this.root.innerHTML = "";
    this.root.className = "co-shell co-mode-" + this.mode;
    applyPageLock(this.mode);

    this.root.appendChild(this.renderShellTop());

    let screen = null;

    if (this.mode === "wow") {
      coTrackOnce(this, "wow_page_view", "wow_page_view", "free_result", Object.assign(
        {},
        getTrackingParams(),
        getTrackingResultMeta(this.engine.state),
        {
          funnel_step: "wow_page_view",
          product: "credit_diagnostic",
          price: 27,
          free_diagnostic_complete: true
        }
      ));

      screen = buildWowScreen(this);
      this.root.appendChild(screen);
      requestAnimationFrame(function () {
        startWowSequence(screen);
      });
      return;
    }

    if (this.mode === "result") {
      coTrackOnce(this, "result_page_view", "result_page_view", "full_diagnostic_result", Object.assign(
        {},
        getTrackingParams(),
        getTrackingResultMeta(this.engine.state),
        {
          funnel_step: "result_page_view",
          product: "credit_diagnostic",
          price: 27,
          unlocked: true
        }
      ));

      screen = buildResultScreen(this);
      this.root.appendChild(screen);
      animateScoreCount(screen);
      this.engine.state.scoreAnimated = true;
      return;
    }


    const currentQuestion = this.engine.getCurrentQuestion ? this.engine.getCurrentQuestion() : null;
    const currentState = this.engine && this.engine.state ? this.engine.state : {};

    trackQuestionViewed(this, currentState, currentQuestion);

    screen = buildQuestionScreen(this);
    this.root.appendChild(screen);
  };

  // ---------------------------------------------------------
  // BASE STYLE INJECTION (JS-SAFE ONLY)
  // Full visual system lives in CSS file
  // ---------------------------------------------------------

  function injectBaseStyles() {
    if (document.getElementById("co-base-styles")) return;

    const style = document.createElement("style");
    style.id = "co-base-styles";

    style.innerHTML = `
      .co-shell { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
      .co-card { border-radius: 20px; }
      .co-option-btn { cursor: pointer; }
      .co-back-btn{
        appearance:none;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        border:1px solid rgba(255,255,255,.16);
        background:rgba(255,255,255,.04);
        color:rgba(220,232,245,.84);
        border-radius:999px;
        padding:8px 12px;
        margin:0 0 18px;
        font-size:13px;
        line-height:1;
        font-weight:800;
        letter-spacing:.02em;
        cursor:pointer;
      }
      .co-back-btn:hover{
        color:#fff;
        border-color:rgba(255,255,255,.28);
        background:rgba(255,255,255,.07);
      }
      @media (max-width: 767px){
        .co-back-btn{
          margin:0 0 14px;
          padding:8px 11px;
          font-size:12px;
        }
      }
      .co-result-row-button { cursor: pointer; }
      .co-result-score-wrap{
        display:flex;
        align-items:flex-end;
        gap:10px;
        margin-top:10px;
      }
      .co-result-score-number{
        font-size:56px;
        line-height:.9;
        font-weight:900;
        letter-spacing:-.05em;
      }
      .co-result-score-label{
        font-size:12px;
        text-transform:uppercase;
        letter-spacing:.12em;
        opacity:.75;
        padding-bottom:4px;
      }
      .co-result-score-sub{
        font-size:13px;
        line-height:1.35;
        opacity:.88;
        padding-bottom:8px;
      }
      .co-next-layer-card{
        margin-top:14px;
        padding:18px;
        border:1px solid rgba(255,255,255,.12);
        border-radius:20px;
        background:rgba(255,255,255,.045);
      }
      .co-next-layer-title{
        margin:6px 0 8px;
        font-size:18px;
        line-height:1.2;
        letter-spacing:-.02em;
      }
      .co-next-layer-body{
        margin:0;
        font-size:14px;
        line-height:1.5;
        opacity:.84;
      }
      .co-result-row-right{
        display:flex;
        align-items:center;
        gap:10px;
        justify-content:flex-end;
      }
      .co-result-row-status-text{
        font-size:11px;
        font-weight:800;
        letter-spacing:.08em;
        text-transform:uppercase;
        opacity:.82;
      }
      .co-result-table-head{
        display:grid;
        grid-template-columns:1fr auto;
        gap:12px;
        padding:0 4px 6px;
        font-size:10px;
        font-weight:800;
        letter-spacing:.14em;
        text-transform:uppercase;
        opacity:.6;
      }
      .co-detail-action{
        margin:8px 0 0;
        font-size:12px;
        line-height:1.45;
        font-weight:700;
        opacity:.9;
      }
      .co-detail-why{
        margin:8px 0 0;
        font-size:12px;
        line-height:1.55;
        opacity:.82;
      }
      .co-wow-card{
        opacity:0;
        transform:scale(.985);
        transition:opacity .65s ease, transform .65s ease;
        min-height:280px;
        display:flex;
        align-items:center;
        justify-content:center;
        flex-direction:column;
        text-align:center;
        padding:28px;
      }
      .co-wow-card.is-visible{ opacity:1; transform:scale(1); }
      .co-wow-text{
        font-size:28px;
        line-height:1.35;
        max-width:460px;
        opacity:.98;
      }
      .co-wow-btn{
        margin-top:24px;
        padding:14px 22px;
        border:none;
        border-radius:12px;
        background:#2f6bff;
        color:#fff;
        font-weight:700;
        cursor:pointer;
      }
      .co-result-insight-card{
        border-radius:18px;
        padding:16px 18px;
        background:rgba(255,255,255,.03);
      }
      .co-result-insight-body{
        margin:0;
        line-height:1.55;
      }
      @media (max-width: 767px){
        .co-wow-text{ font-size:22px; }

        .co-result-score-number{ font-size:46px; }
        .co-result-score-label{ font-size:11px; padding-bottom:4px; }
        .co-result-score-sub{ font-size:12px; }
        .co-next-layer-card{ padding:16px; }
        .co-next-layer-title{ font-size:17px; }
        .co-mini-signal-widget{ width:100%; max-width:none; }
        .co-mini-signal-label{ font-size:11px; }
      }
            .co-shell-wow-mode{
        min-height:100vh;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .co-wow-stage{
        min-height:100vh;
        width:100%;
        display:flex;
        align-items:center;
        justify-content:center;
        padding:24px 18px 36px;
        box-sizing:border-box;
      }
      .co-wow-stage-inner{
        width:100%;
        max-width:980px;
        margin:0 auto;
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:center;
        text-align:center;
      }
      .co-wow-card{
        width:100%;
        max-width:900px;
        margin:0 auto;
        min-height:auto;
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:center;
        text-align:center;
        padding:24px 18px;
        background:transparent;
        box-shadow:none;
        border:none;
      }
      .co-wow-text{
        width:100%;
        max-width:760px;
        margin:0 auto;
        text-align:center;
        font-size:clamp(36px, 5vw, 58px);
        line-height:1.12;
        font-weight:900;
        letter-spacing:-.04em;
      }
      .co-wow-btn{
        margin:26px auto 0;
        padding:14px 22px;
        border:none;
        border-radius:14px;
        background:#2f6bff;
        color:#fff;
        font-weight:800;
        cursor:pointer;
        display:inline-flex;
        align-items:center;
        justify-content:center;
      }

      @media (max-width: 767px){
        .co-wow-stage{
          min-height:calc(100vh - 24px);
          padding:18px 16px 28px;
        }
        .co-wow-stage-inner{
          max-width:100%;
        }
        .co-wow-card{
          max-width:100%;
          padding:12px 6px;
        }
        .co-wow-text{
          max-width:100%;
          font-size:clamp(28px, 8vw, 40px);
          line-height:1.14;
          letter-spacing:-.035em;
        }
        .co-wow-btn{
          width:auto;
          min-width:190px;
          margin-top:22px;
          padding:14px 20px;
          font-size:15px;
        }
      }

      @media (max-width: 430px){
        .co-wow-stage{
          padding:14px 14px 24px;
        }
        .co-wow-text{
          font-size:clamp(24px, 8.4vw, 34px);
          line-height:1.16;
        }
        .co-wow-btn{
          min-width:176px;
          padding:13px 18px;
          font-size:14px;
        }
      }
      .co-wow-reassurance,
      .co-wow-text,
      .co-wow-btn{
        opacity:0;
        transform:translateY(16px);
        transition:opacity .55s ease, transform .55s ease;
      }
      .co-wow-reassurance.is-visible,
      .co-wow-text.is-visible,
      .co-wow-btn.is-visible{
        opacity:1;
        transform:translateY(0);
      }
      .co-wow-reassurance{
        margin:0 0 14px;
        font-size:12px;
        font-weight:800;
        letter-spacing:.14em;
        text-transform:uppercase;
        opacity:.72;
      }
      .co-result-primary-title{
        line-height:1.05;
      }
      .co-result-cta-wrap{
        margin-top:28px;
        max-width:460px;
        margin-left:auto;
        margin-right:auto;
        display:flex;
        flex-direction:column;
        align-items:center;
        text-align:center;
      }
      .co-result-cta-wrap::before{
        content:"";
        display:block;
        width:100%;
        height:1px;
        background:rgba(255,255,255,.08);
        margin-bottom:18px;
      }
      .co-next-product-teaser{
        max-width:420px;
        margin:0 auto 12px;
        font-size:14px;
        line-height:1.5;
        font-weight:650;
        color:#c9d6e3;
        opacity:.86;
      }
      .co-result-disclaimer{
        max-width:430px;
        margin:0 auto;
        font-size:11px;
        line-height:1.45;
        color:#8fa5ba;
        opacity:.68;
      }
      .co-result-continue-btn{
        padding:14px 22px;
        border:none;
        border-radius:14px;
        background:#2f6bff;
        color:#fff;
        font-weight:800;
        cursor:pointer;
      }
      .co-bridge-shell{
        padding:24px 18px 36px;
      }
      .co-bridge-card{
        width:100%;
        max-width:760px;
        margin:0 auto;
        padding:26px 22px;
        border-radius:24px;
        background:linear-gradient(180deg, rgba(7,17,31,.98) 0%, rgba(10,23,36,.98) 100%);
        box-shadow:0 12px 40px rgba(0,0,0,.24);
      }
      .co-bridge-kicker{
        margin:0 0 10px;
        font-size:12px;
        font-weight:800;
        letter-spacing:.14em;
        text-transform:uppercase;
        color:#6dd7a6;
      }
      .co-bridge-title{
        margin:0;
        font-size:clamp(30px,4vw,46px);
        line-height:1.08;
        letter-spacing:-.04em;
      }
      .co-bridge-body{
        margin:14px 0 0;
        font-size:16px;
        line-height:1.6;
        opacity:.9;
        max-width:620px;
      }
      .co-bridge-points{
        display:grid;
        gap:10px;
        margin:22px 0;
      }
      .co-bridge-point{
        padding:12px 14px;
        border-radius:14px;
        background:rgba(109,215,166,.08);
        border:1px solid rgba(109,215,166,.18);
      }
      .co-bridge-btn{
        padding:14px 22px;
        border:none;
        border-radius:14px;
        background:#2f6bff;
        color:#fff;
        font-weight:800;
        cursor:pointer;
      }
      .co-bridge-note{
        margin:14px 0 0;
        font-size:12px;
        opacity:.72;
      }
      @media (max-width: 767px){
        .co-result-primary-title{
          font-size:24px;
          line-height:1.12;
        }
        .co-result-insight-body{
          font-size:14px;
        }
        .co-bridge-card{
          padding:22px 16px;
          border-radius:20px;
        }
        .co-bridge-title{
          font-size:30px;
        }
        .co-bridge-body{
          font-size:15px;
          line-height:1.55;
        }
      }
  
    `;

    document.head.appendChild(style);
  }
    function injectEnhancedStyles() {
    if (document.getElementById("co-enhanced-styles")) return;

    const style = document.createElement("style");
    style.id = "co-enhanced-styles";
    style.innerHTML = `
      .co-mini-signal-widget{
        margin:0 0 14px auto;
        width:min(320px,100%);
        padding:12px 14px;
        border-radius:16px;
        background:rgba(255,255,255,.04);
        border:1px solid rgba(255,255,255,.08);
      }
      .co-mini-signal-kicker{
        display:block;
        font-size:10px;
        font-weight:800;
        letter-spacing:.12em;
        text-transform:uppercase;
        opacity:.7;
        margin-bottom:8px;
      }
      .co-mini-signal-head{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:10px;
      }
      .co-mini-signal-phase{
        font-size:10px;
        font-weight:800;
        letter-spacing:.08em;
        text-transform:uppercase;
        opacity:.72;
      }
      .co-mini-signal-list{
        display:grid;
        gap:9px;
      }
      .co-mini-signal-row{
        display:grid;
        gap:6px;
        transition:transform .28s ease, opacity .28s ease;
      }
      .co-mini-signal-row.is-active{
        transform:translateX(2px);
      }
      .co-mini-signal-label{
        font-size:12px;
        line-height:1.2;
        opacity:.86;
        white-space:normal;
      }
      .co-mini-signal-track{
        display:block;
        height:6px;
        border-radius:999px;
        background:rgba(255,255,255,.08);
        overflow:hidden;
      }
      .co-mini-signal-fill{
        display:block;
        height:100%;
        border-radius:999px;
        background:linear-gradient(90deg,#2f6bff 0%, #6dd7a6 100%);
        transition:width .42s ease;
      }
      @keyframes coMiniPulse {
        0% { opacity:.9; }
        50% { opacity:1; }
        100% { opacity:.9; }
      }
      .co-mini-signal-row.is-active .co-mini-signal-fill{
        animation:coMiniPulse 1.15s ease-in-out infinite;
      }
      .co-result-row-topline{
        display:flex;
        align-items:center;
        gap:10px;
        flex-wrap:wrap;
      }
      .co-result-rank-pill{
        display:inline-flex;
        align-items:center;
        border-radius:999px;
        padding:4px 8px;
        font-size:10px;
        font-weight:800;
        letter-spacing:.06em;
        text-transform:uppercase;
        background:rgba(255,255,255,.08);
        color:rgba(255,255,255,.92);
      }
      .co-rank-main{ background:rgba(47,107,255,.20); color:#dbe7ff; }
      .co-rank-secondary{ background:rgba(255,179,71,.18); color:#ffe0a8; }
      .co-rank-tertiary{ background:rgba(255,255,255,.08); color:#d7deef; }

      .co-result-row-main{ flex:1; min-width:0; }
      .co-result-row-bar{
        width:100%;
        height:6px;
        margin-top:10px;
        border-radius:999px;
        background:rgba(255,255,255,.08);
        overflow:hidden;
      }
      .co-result-row-bar-fill{
        display:block;
        height:100%;
        border-radius:999px;
        background:linear-gradient(90deg, rgba(47,107,255,.95), rgba(118,168,255,.95));
      }

      .co-cine-signal-bars{
        display:grid;
        gap:10px;
        margin:18px 0 14px;
      }
      .co-cine-signal-row{
        display:grid;
        grid-template-columns:minmax(0, 1fr) minmax(120px, 1.3fr);
        gap:12px;
        align-items:center;
      }
      .co-cine-signal-label{
        font-size:12px;
        font-weight:700;
        opacity:.84;
      }
      .co-cine-signal-track{
        display:block;
        height:7px;
        border-radius:999px;
        background:rgba(255,255,255,.08);
        overflow:hidden;
      }
      .co-cine-signal-fill{
        display:block;
        height:100%;
        border-radius:999px;
        background:linear-gradient(90deg, rgba(47,107,255,.92), rgba(118,168,255,.92));
        box-shadow:0 0 18px rgba(47,107,255,.22);
      }

      .co-wow-subsignals{
        width:100%;
        max-width:560px;
        margin:18px auto 4px;
        display:grid;
        gap:10px;
      }
      .co-wow-subitem{
        display:flex;
        align-items:center;
        justify-content:center;
        gap:10px;
        flex-wrap:wrap;
      }
      .co-wow-subtag{
        font-size:10px;
        font-weight:800;
        letter-spacing:.08em;
        text-transform:uppercase;
        opacity:.7;
      }
      .co-wow-subtext{
        font-size:14px;
        font-weight:700;
        opacity:.92;
      }


      .co-wow-card-cliff{
        max-width:680px;
      }
      .co-wow-cliff-copy{
        max-width:560px;
        margin:12px auto 0;
        color:rgba(226,236,255,.80);
        font-size:15px;
        line-height:1.55;
      }
      .co-free-signal-preview{
        width:100%;
        max-width:560px;
        margin:22px auto 0;
        border:1px solid rgba(255,255,255,.10);
        background:rgba(255,255,255,.045);
        border-radius:18px;
        padding:14px;
        text-align:left;
      }
      .co-free-signal-head{
        display:flex;
        justify-content:space-between;
        gap:12px;
        margin-bottom:12px;
        color:rgba(226,236,255,.62);
        font-size:11px;
        font-weight:850;
        text-transform:uppercase;
        letter-spacing:.09em;
      }
      .co-free-signal-list{
        display:grid;
        gap:10px;
      }
      .co-free-signal-row{
        display:grid;
        grid-template-columns:30px 1fr auto;
        align-items:center;
        gap:10px;
        padding:10px;
        border-radius:14px;
        background:rgba(2,9,18,.42);
        border:1px solid rgba(255,255,255,.06);
      }
      .co-free-signal-icon{
        width:26px;
        height:26px;
        display:flex;
        align-items:center;
        justify-content:center;
        border-radius:999px;
        font-size:13px;
        font-weight:950;
      }
      .co-free-signal-label{
        color:#fff;
        font-size:13px;
        font-weight:850;
        margin-bottom:7px;
      }
      .co-free-signal-bar{
        width:100%;
        height:6px;
        border-radius:999px;
        background:rgba(255,255,255,.08);
        overflow:hidden;
      }
      .co-free-signal-bar span{
        display:block;
        height:100%;
        border-radius:999px;
        background:rgba(169,188,255,.78);
      }
      .co-free-signal-status{
        color:rgba(226,236,255,.72);
        font-size:11px;
        font-weight:850;
        text-transform:uppercase;
        letter-spacing:.04em;
        white-space:nowrap;
      }
      .co-wow-locked-strip{
        width:100%;
        max-width:560px;
        margin:18px auto 0;
        padding:12px 14px;
        border-radius:14px;
        background:rgba(111,143,234,.10);
        border:1px solid rgba(169,188,255,.16);
        color:rgba(226,236,255,.78);
        font-size:13px;
        line-height:1.4;
      }
      .co-wow-locked-strip strong{
        color:#fff;
      }
      .co-payment-gate-shell{
        min-height:100svh;
      }
      .co-payment-gate-wrap{
        width:min(760px,100%);
        margin:0 auto;
        padding:24px;
      }
      .co-payment-gate-card{
        padding:30px;
        border:1px solid rgba(255,255,255,.10);
        background:
          radial-gradient(circle at 50% 0%, rgba(111,143,234,.16), transparent 38%),
          rgba(7,17,31,.94);
        box-shadow:0 24px 80px rgba(0,0,0,.32);
        text-align:center;
      }
      .co-payment-title{
        margin:8px auto 0;
        max-width:620px;
        color:#fff;
        font-size:clamp(30px,4vw,48px);
        line-height:1.02;
        letter-spacing:-.055em;
        font-weight:950;
      }
      .co-payment-sub{
        max-width:560px;
        margin:14px auto 0;
        color:rgba(226,236,255,.80);
        font-size:15px;
        line-height:1.55;
      }
      .co-locked-preview-grid{
        max-width:560px;
        margin:18px auto 0;
        display:grid;
        gap:10px;
      }
      .co-locked-preview-card{
        display:grid;
        grid-template-columns:32px 1fr;
        gap:10px;
        padding:13px;
        border-radius:16px;
        border:1px solid rgba(255,255,255,.08);
        background:rgba(255,255,255,.045);
        text-align:left;
        position:relative;
        overflow:hidden;
      }
      .co-locked-preview-card:after{
        content:"";
        position:absolute;
        inset:0;
        backdrop-filter:blur(1px);
        background:linear-gradient(90deg, transparent, rgba(2,9,18,.16));
        pointer-events:none;
      }
      .co-locked-preview-lock{
        width:30px;
        height:30px;
        display:flex;
        align-items:center;
        justify-content:center;
        border-radius:999px;
        background:rgba(255,255,255,.08);
        font-size:13px;
      }
      .co-locked-preview-card strong{
        display:block;
        color:#fff;
        font-size:13px;
        font-weight:900;
      }
      .co-locked-preview-card p{
        margin:4px 0 0;
        color:rgba(226,236,255,.66);
        font-size:12px;
        line-height:1.45;
      }
      .co-payment-cta{
        appearance:none;
        border:0;
        width:min(100%,420px);
        margin:22px auto 0;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        padding:17px 20px;
        border-radius:16px;
        background:#fff;
        color:#020912;
        font-size:16px;
        font-weight:950;
        cursor:pointer;
        box-shadow:0 18px 44px rgba(255,255,255,.12),0 10px 36px rgba(111,143,234,.28);
      }
      .co-payment-trust{
        margin-top:14px;
        color:rgba(226,236,255,.58);
        font-size:12px;
        line-height:1.45;
      }

      @media (max-width: 767px){
        .co-cine-signal-row{
          grid-template-columns:1fr;
          gap:8px;
        }
        .co-wow-subtext{ font-size:13px; }
      }
    `;
    document.head.appendChild(style);
  }

  function injectWarmQuestionStyles() {
    if (document.getElementById("co-warm-question-styles")) return;

    const style = document.createElement("style");
    style.id = "co-warm-question-styles";
    style.innerHTML = `
      .co-shell{
        background:
          radial-gradient(circle at 50% -8%, rgba(120,151,255,.18), transparent 36%),
          radial-gradient(circle at 16% 12%, rgba(109,215,166,.08), transparent 28%),
          linear-gradient(180deg,#06101e 0%,#071424 46%,#040a13 100%) !important;
      }

      .co-shell-top-warm{
        backdrop-filter:blur(18px);
        background:rgba(4,10,19,.56);
        border-bottom:1px solid rgba(255,255,255,.07);
      }

      .co-shell-badge-warm{
        background:rgba(255,255,255,.065);
        color:rgba(235,241,255,.9);
        border:1px solid rgba(255,255,255,.10);
      }

      .co-progress-wrap-warm .co-progress-track{
        background:rgba(255,255,255,.065);
        box-shadow:inset 0 0 0 1px rgba(255,255,255,.035);
      }

      .co-progress-wrap-warm .co-progress-fill{
        background:linear-gradient(90deg,#6f8fea 0%,#9fb4ff 55%,#6dd7a6 100%);
        box-shadow:0 0 24px rgba(111,143,234,.38);
      }

      .co-question-screen{
        min-height:calc(100svh - 88px);
        display:flex;
        align-items:center;
        justify-content:center;
        padding:26px 18px 44px;
        box-sizing:border-box;
      }

      .co-q-card-emotional{
        width:min(940px,100%);
        margin:0 auto;
        padding:34px;
        border:1px solid rgba(255,255,255,.10);
        background:
          radial-gradient(circle at 50% 0%, rgba(111,143,234,.16), transparent 34%),
          linear-gradient(180deg, rgba(10,24,42,.92), rgba(5,13,24,.94));
        box-shadow:0 28px 90px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.05);
      }

      .co-first-question-screen .co-q-card-emotional{
        max-width:980px;
        padding:42px 40px 36px;
      }

      .co-question-wrap-emotional{
        max-width:760px;
        margin:0 auto;
      }

      .co-question-topline{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:14px;
        margin-bottom:14px;
      }

      .co-warm-kicker{
        color:#aebdfd;
        letter-spacing:.14em;
      }

      .co-time-pill{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        padding:7px 11px;
        border-radius:999px;
        background:rgba(255,255,255,.065);
        border:1px solid rgba(255,255,255,.10);
        color:rgba(235,241,255,.76);
        font-size:11px;
        font-weight:850;
        letter-spacing:.06em;
        text-transform:uppercase;
        white-space:nowrap;
      }

      .co-first-orientation{
        text-align:left;
        margin-bottom:22px;
      }

      .co-q-title-hero{
        max-width:760px;
        margin:0;
        font-size:clamp(34px,5.2vw,62px);
        line-height:1.02;
        letter-spacing:-.06em;
        font-weight:950;
        color:#fff;
      }

      .co-q-intro{
        max-width:620px;
        margin:12px 0 0;
        color:rgba(226,236,255,.78);
        font-size:15px;
        line-height:1.62;
      }

      .co-q-intro-hero{
        max-width:700px;
        margin-top:16px;
        font-size:17px;
        line-height:1.6;
        color:rgba(236,242,255,.82);
      }

      .co-trust-strip{
        display:flex;
        align-items:center;
        flex-wrap:wrap;
        gap:8px;
        margin-top:18px;
      }

      .co-trust-strip span,
      .co-soft-progress-row{
        display:inline-flex;
        align-items:center;
        gap:7px;
        padding:8px 10px;
        border-radius:999px;
        background:rgba(255,255,255,.055);
        border:1px solid rgba(255,255,255,.08);
        color:rgba(232,239,255,.76);
        font-size:12px;
        line-height:1;
        font-weight:800;
      }

      .co-soft-progress-dot{
        width:7px;
        height:7px;
        border-radius:999px;
        background:#6dd7a6;
        box-shadow:0 0 14px rgba(109,215,166,.55);
        flex:0 0 auto;
      }

      .co-question-copy-block{
        margin-bottom:18px;
      }

      .co-micro-bridge-warm{
        border-left:2px solid rgba(159,180,255,.55);
        padding-left:12px;
        color:rgba(226,236,255,.72);
      }

      .co-first-question-label{
        margin:22px 0 12px;
        color:rgba(235,241,255,.72);
        font-size:12px;
        font-weight:900;
        text-transform:uppercase;
        letter-spacing:.13em;
      }

      .co-option-grid-emotional{
        display:grid;
        gap:12px;
      }

      .co-option-btn-emotional{
        display:grid;
        grid-template-columns:30px 1fr auto;
        align-items:center;
        gap:12px;
        width:100%;
        padding:16px 17px;
        border-radius:18px;
        background:rgba(255,255,255,.055);
        border:1px solid rgba(255,255,255,.10);
        color:#fff;
        text-align:left;
        box-shadow:0 10px 30px rgba(0,0,0,.12);
        transition:transform .18s ease, border-color .18s ease, background .18s ease, box-shadow .18s ease;
      }

      .co-option-btn-emotional:hover{
        transform:translateY(-1px);
        background:rgba(255,255,255,.085);
        border-color:rgba(159,180,255,.34);
        box-shadow:0 18px 42px rgba(0,0,0,.18),0 0 0 1px rgba(159,180,255,.08);
      }

      .co-option-btn-emotional:active{
        transform:translateY(0) scale(.995);
      }

      .co-option-number{
        width:28px;
        height:28px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        border-radius:999px;
        background:rgba(159,180,255,.12);
        color:#cbd6ff;
        border:1px solid rgba(159,180,255,.16);
        font-size:12px;
        font-weight:900;
      }

      .co-option-btn-emotional .co-option-label{
        font-size:15px;
        line-height:1.25;
        font-weight:850;
        color:rgba(255,255,255,.94);
      }

      .co-option-btn-emotional .co-option-arrow{
        color:#9fb4ff;
        font-size:24px;
        line-height:1;
        opacity:.9;
      }

      @media (max-width: 767px){
        .co-question-screen{
          min-height:calc(100svh - 74px);
          padding:16px 14px 26px;
          align-items:flex-start;
        }

        .co-q-card-emotional,
        .co-first-question-screen .co-q-card-emotional{
          padding:22px 16px 18px;
          border-radius:24px;
        }

        .co-question-topline{
          margin-bottom:12px;
        }

        .co-time-pill{
          display:none;
        }

        .co-q-title-hero{
          font-size:clamp(31px,9.2vw,42px);
          line-height:1.06;
          letter-spacing:-.052em;
        }

        .co-q-title{
          font-size:clamp(25px,7.3vw,34px);
          line-height:1.08;
          letter-spacing:-.045em;
        }

        .co-q-intro,
        .co-q-intro-hero{
          font-size:14px;
          line-height:1.52;
          margin-top:12px;
        }

        .co-trust-strip{
          gap:7px;
          margin-top:14px;
        }

        .co-trust-strip span,
        .co-soft-progress-row{
          font-size:11px;
          padding:7px 9px;
        }

        .co-first-question-label{
          margin:18px 0 10px;
          font-size:11px;
        }

        .co-option-grid-emotional{
          gap:10px;
        }

        .co-option-btn-emotional{
          grid-template-columns:28px 1fr auto;
          padding:14px 14px;
          border-radius:16px;
        }

        .co-option-number{
          width:26px;
          height:26px;
          font-size:11px;
        }

        .co-option-btn-emotional .co-option-label{
          font-size:14px;
        }
      }

      @media (max-width: 430px), (max-height: 760px){
        .co-question-screen{
          padding-top:12px;
        }

        .co-q-card-emotional,
        .co-first-question-screen .co-q-card-emotional{
          padding:18px 14px 15px;
        }

        .co-q-title-hero{
          font-size:clamp(28px,8.4vw,36px);
        }

        .co-q-intro-hero{
          font-size:13px;
        }

        .co-trust-strip span:nth-child(3){
          display:none;
        }

        .co-option-btn-emotional{
          padding:12px 12px;
        }
      }
    `;
    document.head.appendChild(style);
  }


  function injectFinalLaunchStyles() {
    if (document.getElementById("co-final-launch-styles")) return;

    const style = document.createElement("style");
    style.id = "co-final-launch-styles";
    style.innerHTML = `


      html.co-page-scrollable,
      body.co-page-scrollable{
        height:auto !important;
        overflow-x:hidden !important;
        overflow-y:auto !important;
      }



      .co-shell.co-mode-result{
        min-height:100svh !important;
        overflow-x:hidden !important;
        overflow-y:auto !important;
      }

      .co-shell{
        background:
          radial-gradient(circle at 50% -6%, rgba(111,143,234,.22), transparent 37%),
          radial-gradient(circle at 14% 18%, rgba(109,215,166,.08), transparent 27%),
          radial-gradient(circle at 88% 28%, rgba(243,191,116,.055), transparent 31%),
          linear-gradient(180deg,#030a14 0%,#061423 52%,#020711 100%) !important;
      }

      .co-shell-top{
        background:transparent !important;
        border:0 !important;
        box-shadow:none !important;
      }

      .co-card,
      .co-q-card,
      .co-q-card-emotional,
      .co-question-wrap,
      .co-question-wrap-emotional{
        background:transparent !important;
        border:0 !important;
        box-shadow:none !important;
        border-radius:0 !important;
      }

      .co-card::before,
      .co-card::after,
      .co-q-card::before,
      .co-q-card::after,
      .co-q-card-emotional::before,
      .co-q-card-emotional::after,
      .co-question-wrap::before,
      .co-question-wrap::after,
      .co-question-wrap-emotional::before,
      .co-question-wrap-emotional::after{
        content:none !important;
        display:none !important;
      }

      .co-question-screen{
        height:calc(100svh - 84px) !important;
        min-height:0 !important;
        display:flex !important;
        align-items:center !important;
        justify-content:center !important;
        padding:18px 22px 24px !important;
        overflow:hidden !important;
      }

      .co-question-screen::before{
        content:"";
        position:absolute;
        left:50%;
        top:48%;
        width:min(980px,92vw);
        height:min(620px,72vh);
        transform:translate(-50%,-50%);
        pointer-events:none;
        background:
          radial-gradient(circle at 50% 36%, rgba(111,143,234,.11), transparent 40%),
          radial-gradient(circle at 22% 72%, rgba(109,215,166,.055), transparent 26%);
        filter:blur(2px);
        opacity:.95;
      }

      .co-q-card,
      .co-q-card-emotional{
        width:min(980px,100%) !important;
        margin:0 auto !important;
        padding:0 !important;
      }

      .co-question-wrap,
      .co-question-wrap-emotional{
        width:100% !important;
        max-width:900px !important;
        margin:0 auto !important;
        padding:0 !important;
        display:flex !important;
        flex-direction:column !important;
      }

      .co-first-question-screen .co-q-card,
      .co-first-question-screen .co-q-card-emotional{
        padding:0 !important;
      }

      .co-q-title{
        max-width:12.5ch !important;
        font-size:clamp(36px,5vw,68px) !important;
        line-height:.98 !important;
        letter-spacing:-.065em !important;
        margin:0 0 14px !important;
      }

      .co-q-title-hero{
        max-width:790px !important;
        font-size:clamp(42px,6vw,78px) !important;
        line-height:.98 !important;
        letter-spacing:-.075em !important;
      }

      .co-q-intro,
      .co-q-intro-hero{
        color:rgba(236,242,255,.76) !important;
      }

      .co-option-grid{
        width:min(760px,100%) !important;
        display:grid !important;
        gap:11px !important;
        margin-top:15px !important;
      }

      .co-first-question-screen .co-option-grid{
        margin-top:0 !important;
      }

      .co-option-btn,
      .co-option-btn-emotional{
        position:relative !important;
        overflow:hidden !important;
        min-height:62px !important;
        display:grid !important;
        grid-template-columns:30px 1fr auto !important;
        align-items:center !important;
        gap:13px !important;
        padding:15px 18px !important;
        border-radius:0 !important;
        color:#fff !important;
        background:rgba(255,255,255,.018) !important;
        border:1px solid rgba(255,255,255,.085) !important;
        box-shadow:none !important;
      }

      .co-option-btn::before,
      .co-option-btn-emotional::before{
        content:"" !important;
        display:block !important;
        position:absolute !important;
        left:0 !important;
        top:12px !important;
        bottom:12px !important;
        width:2px !important;
        background:transparent !important;
        transition:background .20s ease, box-shadow .20s ease !important;
      }

      .co-option-btn::after,
      .co-option-btn-emotional::after{
        content:"" !important;
        display:block !important;
        position:absolute !important;
        inset:0 !important;
        pointer-events:none !important;
        background:linear-gradient(90deg, rgba(111,143,234,.075), transparent 28%) !important;
        opacity:0 !important;
        transition:opacity .20s ease !important;
      }

      .co-option-btn:hover,
      .co-option-btn-emotional:hover{
        transform:translateY(-1px) !important;
        border-color:rgba(159,180,255,.28) !important;
        background:rgba(111,143,234,.055) !important;
      }

      .co-option-btn:hover::before,
      .co-option-btn-emotional:hover::before{
        background:linear-gradient(180deg,#9fb4ff,#6f8fea) !important;
        box-shadow:0 0 14px rgba(111,143,234,.36) !important;
      }

      .co-option-btn:hover::after,
      .co-option-btn-emotional:hover::after{
        opacity:1 !important;
      }

      .co-option-number{
        background:rgba(159,180,255,.09) !important;
        border:1px solid rgba(159,180,255,.13) !important;
      }

      .co-option-label{
        font-size:16px !important;
        font-weight:760 !important;
        line-height:1.24 !important;
      }

      .co-trust-strip span,
      .co-soft-progress-row,
      .co-time-pill,
      .co-shell-badge{
        background:rgba(255,255,255,.035) !important;
        border:1px solid rgba(255,255,255,.075) !important;
        box-shadow:none !important;
      }

      .co-cine-card,
      .co-wow-card,
      .co-payment-gate-card{
        background:transparent !important;
        border:0 !important;
        box-shadow:none !important;
        border-radius:0 !important;
        padding:0 !important;
      }

      .co-wow-stage,
      .co-payment-gate-shell,
      .co-cinematic-shell{
        height:calc(100svh - 84px) !important;
        min-height:0 !important;
        overflow:hidden !important;
      }

      .co-cine-title,
      .co-payment-title,
      .co-wow-text{
        letter-spacing:-.07em !important;
      }

      .co-free-signal-preview,
      .co-locked-preview-card{
        background:transparent !important;
        border-radius:0 !important;
        box-shadow:none !important;
      }

      @media (max-width:767px){
        .co-question-screen{
          align-items:flex-start !important;
          height:calc(100svh - 66px) !important;
          padding:16px 16px 20px !important;
        }

        .co-question-wrap,
        .co-question-wrap-emotional{
          justify-content:flex-start !important;
        }

        .co-q-title,
        .co-q-title-hero{
          max-width:12ch !important;
          font-size:clamp(31px,9vw,42px) !important;
          line-height:1.02 !important;
        }

        .co-q-intro,
        .co-q-intro-hero{
          font-size:13.5px !important;
          line-height:1.45 !important;
        }

        .co-trust-strip{
          margin-top:12px !important;
        }

        .co-first-question-label{
          margin:15px 0 9px !important;
        }

        .co-option-grid{
          width:100% !important;
          gap:9px !important;
        }

        .co-option-btn,
        .co-option-btn-emotional{
          min-height:54px !important;
          grid-template-columns:28px 1fr auto !important;
          padding:12px 0 12px 12px !important;
        }

        .co-option-label{
          font-size:13.5px !important;
        }
      }

      @media (max-width:430px), (max-height:760px){
        .co-question-screen{
          padding:12px 14px 16px !important;
        }

        .co-q-title,
        .co-q-title-hero{
          font-size:clamp(28px,8.8vw,36px) !important;
        }

        .co-q-intro,
        .co-q-intro-hero{
          font-size:12.75px !important;
        }

        .co-trust-strip span:nth-child(3){
          display:none !important;
        }

        .co-option-btn,
        .co-option-btn-emotional{
          min-height:50px !important;
          padding:10px 0 10px 10px !important;
        }

        .co-option-label{
          font-size:13px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------
  // SAFE MOUNT / INIT
  // ---------------------------------------------------------

  function mountApp(selector) {
    const root =
      (selector ? document.querySelector(selector) : null) ||
      document.getElementById("coExecApp") ||
      document.getElementById("coAppShell");

    if (!root) {
      console.warn("Credit Optimize: root container not found");
      return null;
    }

    if (root.__coMountedApp) {
      return root.__coMountedApp;
    }

    try {
      const app = new CreditOptimizePaidApp(root);
      root.__coMountedApp = app;
      global.__CO_APP__ = app;
      app.render();
      return app;
    } catch (err) {
      console.error("Credit Optimize failed to initialize:", err);

      root.innerHTML = `
        <div style="padding:20px;font-family:sans-serif;color:#fff;background:#07111f;border-radius:16px;">
          <h2 style="margin:0 0 8px;">Something went wrong</h2>
          <p style="margin:0;">Please refresh the page or try again.</p>
        </div>
      `;
      return null;
    }
  }

  global.CreditOptimize = global.CreditOptimize || {};
  global.CreditOptimize.init = function (selector) {
    return mountApp(selector || "#coExecApp");
  };

  // ---------------------------------------------------------
  // INIT (DOM READY)
  // ---------------------------------------------------------

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      mountApp("#coExecApp");
    });
  } else {
    mountApp("#coExecApp");
  }

})(window);